import {
  AlertCircle,
  ArrowLeft,
  BellRing,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock3,
  Cloud,
  CloudOff,
  Euro,
  FileText,
  Home,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  PhoneCall,
  Plus,
  Search,
  Settings as SettingsIcon,
  Shield,
  Sparkles,
  StickyNote,
  Target,
  Trash2,
  User,
  Users,
  XCircle
} from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { SyncStatus } from "./lib/caseSync";
import type { PushPermissionStatus } from "./lib/usePushNotifications";
import type { PushPermissionLabel } from "./pushNotificationStatus";
import {
  nextActionKind,
  outcomeNextUiState,
  type NextActionUiState
} from "./nextActionUi";
import { canAddMissingNextStep, isAgendaExcludedCase, isClosedLostCase, isExcludedCase, isLiveCase, isWaitingCase } from "./caseLifecycle";
import {
  nextActionBucket,
  nextActionCompletionLabel,
  nextManualStepDraft,
  overdueTimeLabel
} from "./nextActionEngine";
import { canStartListingWorkflow, listingActionId } from "./listingWorkflow";
import { shotScoreValue, type ShotScoreAnalytics } from "./shotScore";
import {
  CaseMarketAnalysisEntry,
  marketAnalysisTimelineActivities
} from "./features/market/components/CaseMarketAnalysisEntry";
import { useMarketAnalysisContext } from "./features/market/sync/useMarketAnalysisSync";
import { MarketTimelinePanel } from "./features/marketTimeline/MarketTimelinePanel";
import { DebriefGateCard } from "./DebriefGateCard";
import type { MarketStatsInput } from "./features/marketTimeline/types";
import { CityAutocomplete } from "./features/market/location/CityAutocomplete";
import { StreetAutocomplete } from "./features/market/location/StreetAutocomplete";
import { locationAfterCityChange } from "./features/market/location/streetSearch";
import { stableCaseImageIndex, stableCaseImageSources } from "./propertyImage";
import { classifyDebriefEvent, debriefEventLabel, getPendingRequiredDebrief, isDebriefPurpose } from "./debrief";
import {
  apartmentPropertyImages,
  commercialPropertyImages,
  housePropertyImages,
  landPropertyImages,
  legacyPropertyImagePools,
  propertyImageSources,
  todayHeroImage
} from "./propertyImageAssets";
import {
  caseContextLabel,
  formatCurrency,
  formatDate,
  formatDateTime,
  nextStepLabel,
  propertyDisplayLabel
} from "./format";
import {
  createOwnerSaleFollowUpDraft,
  getCurrentOwnerSaleSequenceStep,
  getNextOwnerSaleSequenceStep,
  getOwnerSaleSequenceStep,
  isOwnerSaleSequenceActive,
  isOwnerSaleSequenceEnded,
  ownerSaleProgressLabel
} from "./ownerSaleSequence";
import {
  isReservationControl,
  RESERVATION_DUE_LABEL,
  type ReservationOutcome
} from "./reservationWorkflow";
import {
  OWNER_SALE_SEQUENCE_END_REASONS,
  APARTMENT_ROOM_OPTIONS,
  CASE_STAGE_OPTIONS,
  COOPERATION_TYPES,
  OUTCOMES,
  PROPERTY_TYPES,
  TRANSACTION_TYPES,
  type ApartmentRooms,
  type CallOutcome,
  type CaseStage,
  type CooperationType,
  type DebriefReminder,
  type PropertyType,
  type RealEstateCase,
  type TimelineActivity,
  type TransactionType
} from "./types";
import type {
  CaseListFilter,
  CaseEditDraft,
  CaseLocationDraft,
  ClosingDraft,
  FollowUpCompletion,
  NewCaseDraft,
  NextStepDraft,
  OwnerSaleContactDraft,
  OwnerSaleEndDraft,
  OutcomeConfigMap,
  OutcomeDraft,
  PropertyFilter,
  RejectionDraft,
  SoldDraft,
  Screen,
  ShotScoreDraft,
  TodayFilter,
  TransactionFilter
} from "./uiTypes";
import type { WaitingOutcome } from "./waitingWorkflow";

const caseFilters: CaseListFilter[] = [
  "Aktívne",
  "Čakajúce",
  "HOT",
  "Follow-up",
  "Stretnutia",
  "Archív",
  "Bez ďalšieho kroku"
];

const todayFilters: TodayFilter[] = ["Všetko", "Omeškané", "Dnes", "Nadchádzajúce", "Bez kroku"];
const dailyMottos = [
  "Aj keď cítim strach, hanbu či vinu, konám odvážne a hneď.",
  "Boh ma odmení, keď som odvážny a konám.",
  "Každá neponúknutá služba je strela, ktorú som vôbec nevystrelil.",
  "Ja sa s každým problémom určite vyrovnám. Ja to určite dokážem."
] as const;

const dailyMottoForDate = (date: string) => {
  const [year, month, day] = date.split("-").map(Number);
  const localCalendarDay = Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
  return dailyMottos[localCalendarDay % dailyMottos.length];
};

type LoginScreenProps = {
  onLogin: (email: string, password: string) => Promise<void>;
};

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await onLogin(email.trim(), password);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Prihlásenie sa nepodarilo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-shell auth-shell">
      <main className="login-screen">
        <section className="login-panel">
          <div className="brand-lockup" aria-label="FISPRO OS">
            <div className="brand-mark" aria-hidden="true">
              <span>F</span>
            </div>
            <div>
              <p className="eyebrow">FISPRO OS</p>
              <h1>Prihlásenie</h1>
            </div>
          </div>

          <div className="login-copy">
            <h2>Jeden účet. Všetky zariadenia.</h2>
            <p>Prihlás sa účtom vytvoreným v Supabase Auth.</p>
          </div>

          <form className="login-form" onSubmit={submit}>
            <label className="text-field">
              <span>E-mail</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                inputMode="email"
                required
              />
            </label>
            <label className="text-field">
              <span>Heslo</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button
              className="primary-button full-button"
              type="submit"
              disabled={isSubmitting || !email.trim() || !password}
            >
              <KeyRound size={18} aria-hidden="true" />
              {isSubmitting ? "Prihlasujem..." : "Prihlásiť sa"}
            </button>
          </form>

          <div className="login-feedback" aria-live="polite">
            {error ? <p className="login-error">{error}</p> : null}
          </div>
        </section>
      </main>
    </div>
  );
}

type PasswordRecoveryScreenProps = {
  onUpdatePassword: (password: string) => Promise<void>;
  onComplete: () => void;
};

export function PasswordRecoveryScreen({ onUpdatePassword, onComplete }: PasswordRecoveryScreenProps) {
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const completionTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (completionTimerRef.current !== null) {
        window.clearTimeout(completionTimerRef.current);
      }
    };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!password || !passwordConfirmation) {
      setError("Vyplň obe polia hesla.");
      return;
    }

    if (password.length < 8) {
      setError("Heslo musí mať aspoň 8 znakov.");
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Heslá sa musia zhodovať.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdatePassword(password);
      setSuccess("Heslo bolo úspešne zmenené.");
      completionTimerRef.current = window.setTimeout(onComplete, 1400);
    } catch {
      setError("Heslo sa nepodarilo zmeniť. Skontroluj pripojenie alebo si pošli nový odkaz na obnovu hesla.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-shell auth-shell">
      <main className="login-screen">
        <section className="login-panel">
          <div className="brand-lockup" aria-label="FISPRO OS">
            <div className="brand-mark" aria-hidden="true">
              <span>F</span>
            </div>
            <div>
              <p className="eyebrow">FISPRO OS</p>
              <h1>Nastaviť nové heslo</h1>
            </div>
          </div>

          <form className="login-form" onSubmit={submit}>
            <label className="text-field">
              <span>Nové heslo</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </label>
            <label className="text-field">
              <span>Zopakovať nové heslo</span>
              <input
                type="password"
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </label>
            <button
              className="primary-button full-button"
              type="submit"
              disabled={isSubmitting || Boolean(success)}
            >
              <KeyRound size={18} aria-hidden="true" />
              {isSubmitting ? "Ukladám..." : "Uložiť nové heslo"}
            </button>
          </form>

          <div className="login-feedback" aria-live="polite">
            {error ? <p className="login-error">{error}</p> : null}
            {success ? <p className="login-success">{success}</p> : null}
          </div>
        </section>
      </main>
    </div>
  );
}

const pipelineGroups: Array<{
  title: string;
  stages: CaseStage[];
  icon: LucideIcon;
  description: string;
}> = [
  {
    title: "Nový / Cold call",
    stages: ["Nový", "Prvý cold call"],
    icon: PhoneCall,
    description: "Nové kontakty a prvé volania."
  },
  {
    title: "Follow-up",
    stages: ["Kontaktovaný", "Follow-up", "Rozmýšľa"],
    icon: Clock3,
    description: "Presné ďalšie hovory a rozhodnutia."
  },
  {
    title: "Stretnutie",
    stages: ["Stretnutie"],
    icon: CalendarDays,
    description: "Dohodnuté osobné alebo online stretnutia."
  },
  {
    title: "Aktívne",
    stages: ["Aktívne", "Obhliadka", "Rokovanie"],
    icon: Sparkles,
    description: "Aktívna obchodná práca, rozhodnutia a posun k náboru."
  },
  {
    title: "Zainzerované",
    stages: ["Zainzerované"],
    icon: Building2,
    description: "Ponuky zverejnené a aktívne na trhu."
  },
  {
    title: "Rezervované",
    stages: ["Rezervované"],
    icon: KeyRound,
    description: "Prípady s potvrdenou rezerváciou."
  }
];

const terminalStages: CaseStage[] = ["Predané", "Uzavretý", "Vyradený", "Stratené"];

const caseImageSourcePools = {
  all: propertyImageSources,
  preferred: {
    apartment: apartmentPropertyImages,
    house: housePropertyImages,
    land: landPropertyImages,
    commercial: commercialPropertyImages,
    general: propertyImageSources
  },
  legacy: legacyPropertyImagePools
} as const;

const CaseImageSourceContext = createContext<ReadonlyMap<string, string> | null>(null);

export function CaseImageProvider({ cases, children }: { cases: RealEstateCase[]; children: ReactNode }) {
  const sources = useMemo(
    () =>
      stableCaseImageSources(
        cases.map((caseItem) => ({
          id: caseItem.id,
          createdAt: caseItem.createdAt,
          group: propertyImageGroup(caseItem.propertyType),
          legacyGroup: legacyPropertyImageGroup(caseItem.propertyType)
        })),
        caseImageSourcePools
      ),
    [cases]
  );

  return <CaseImageSourceContext.Provider value={sources}>{children}</CaseImageSourceContext.Provider>;
}

const coldCallClosingReminder =
  "Nechcem vás presviedčať, aby ste dnes menili názor. Skúste si to podľa svojich predstáv. Ja mám iba jeden návrh. Trh býva najúprimnejší počas prvých 3 dní. Dohodnime si už teraz krátky dvojminútový telefonát vo štvrtok. Ak budete spokojná, poviete mi to, ja vám zagratulujem. Ak nie, možno nájdeme riešenie. Vyhovuje vám 10:00 alebo 17:00?";

const isMissingNextStep = (caseItem: RealEstateCase) =>
  canAddMissingNextStep(caseItem) && !isOwnerSaleSequenceEnded(caseItem);

const caseStateLabel = (caseItem: RealEstateCase) =>
  isExcludedCase(caseItem) ? "Vyradený" : isWaitingCase(caseItem) ? "Čakajúci" : caseItem.status;

const createOwnerSalePlanDraft = (caseItem: RealEstateCase, today: string): NextStepDraft => {
  const currentStep = getCurrentOwnerSaleSequenceStep(caseItem);
  const fallbackFollowUp = currentStep
    ? createOwnerSaleFollowUpDraft(currentStep, caseItem.followUp?.time ?? "09:00")
    : undefined;
  const followUp = caseItem.followUp ?? fallbackFollowUp;

  return {
    date: followUp?.date ?? today,
    time: followUp?.time ?? "09:00",
    purpose: followUp?.purpose ?? currentStep?.title ?? "Doplniť ďalší kontakt"
  };
};

const datePlusDays = (date: string, days: number) => {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + days);
  return [value.getFullYear(), String(value.getMonth() + 1).padStart(2, "0"), String(value.getDate()).padStart(2, "0")].join("-");
};

type NextActionSchedulerProps = {
  today: string;
  value: NextStepDraft;
  onChange: (value: NextStepDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
  saveDisabled?: boolean;
};

export function NextActionScheduler({ today, value, onChange, onSave, onCancel, saveLabel, saveDisabled = false }: NextActionSchedulerProps) {
  const quickDates = [
    { label: "Dnes", value: today },
    { label: "Zajtra", value: datePlusDays(today, 1) },
    { label: "+3 dni", value: datePlusDays(today, 3) },
    { label: "+7 dní", value: datePlusDays(today, 7) }
  ];
  const debriefEvent = isDebriefPurpose(value.purpose);
  const endTimeValid = !value.endTime || value.endTime > value.time;
  const canSave = Boolean(value.date && value.time && value.purpose.trim() && endTimeValid);

  return (
    <div className="next-action-scheduler" aria-label="Naplánovanie ďalšieho kroku">
      <div className="scheduler-quick-dates" aria-label="Rýchly výber dátumu">
        {quickDates.map((option) => (
          <button
            className={value.date === option.value ? "active" : ""}
            key={option.label}
            type="button"
            onClick={() => onChange({ ...value, date: option.value })}
          >
            {option.label}
          </button>
        ))}
        <label className="scheduler-custom-date">
          <span>Vlastný termín</span>
          <input
            aria-label="Vlastný dátum"
            type="date"
            value={value.date}
            onChange={(event) => onChange({ ...value, date: event.target.value })}
          />
        </label>
      </div>
      <div className="form-grid scheduler-fields">
        <TextField
          label="Čas"
          type="time"
          value={value.time}
          onChange={(time) => onChange({ ...value, time })}
          required
        />
        <TextField
          label="Účel"
          value={value.purpose}
          onChange={(purpose) => onChange({ ...value, purpose })}
          required
        />
      </div>
      {debriefEvent ? (
        <div className="scheduler-end-time">
          <TextField
            label="Koniec stretnutia (voliteľné)"
            type="time"
            value={value.endTime ?? ""}
            onChange={(endTime) => onChange({ ...value, endTime: endTime || undefined })}
          />
          <small>
            {value.endTime
              ? endTimeValid ? "Debrief sa pripomenie po tomto čase." : "Koniec musí byť neskôr ako začiatok."
              : "Bez času použije FISPRO predvolenú dĺžku a označí ju ako odvodenú."}
          </small>
        </div>
      ) : null}
      <div className="button-row">
        <button className="secondary-button" type="button" onClick={onCancel}>Zrušiť</button>
        <button className="primary-button" type="button" onClick={onSave} disabled={!canSave || saveDisabled}>{saveLabel}</button>
      </div>
    </div>
  );
}

const createOwnerSaleContactDraft = (caseItem: RealEstateCase): OwnerSaleContactDraft => {
  const currentStep = getCurrentOwnerSaleSequenceStep(caseItem);
  const nextStep = getNextOwnerSaleSequenceStep(currentStep?.step);
  const preferredTime = caseItem.followUp?.time ?? "09:00";
  const nextFollowUp = nextStep ? createOwnerSaleFollowUpDraft(nextStep, preferredTime) : undefined;

  return {
    result: "",
    note: "",
    nextDate: nextFollowUp?.date ?? "",
    nextTime: nextFollowUp?.time ?? preferredTime,
    nextPurpose: nextFollowUp?.purpose ?? "",
    endReason: "Sekvencia ukončená manuálne"
  };
};

type AppHeaderProps = {
  screen: Screen;
  onBackToCases: () => void;
};

export function AppHeader({ screen, onBackToCases }: AppHeaderProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <header className="topbar">
      <div className="brand-lockup" aria-label="FISPRO OS">
        <div className="brand-mark" aria-hidden="true">
          <span>F</span>
        </div>
        <div>
          <p className="eyebrow">FISPRO OS</p>
          <h1>{screen === "detail" ? "Prípad detail" : screenTitle(screen)}</h1>
        </div>
      </div>

      <div className="header-actions">
        <div className="header-clock" aria-label="Aktuálny čas a dátum">
          <time dateTime={now.toISOString()}>
            {now.toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit" })}
          </time>
          <span>{now.toLocaleDateString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
        </div>

        {screen === "detail" ? (
          <button className="icon-button" type="button" onClick={onBackToCases} title="Späť na prípady">
            <ArrowLeft size={18} aria-hidden="true" />
            <span className="sr-only">Späť na prípady</span>
          </button>
        ) : null}
      </div>
    </header>
  );
}

type BottomNavigationProps = {
  screen: Screen;
  onNavigate: (screen: Screen) => void;
};

export function BottomNavigation({ screen, onNavigate }: BottomNavigationProps) {
  return (
    <nav className="bottom-nav" aria-label="Hlavná navigácia">
      <NavButton active={screen === "now"} icon={Target} label="Teraz" onClick={() => onNavigate("now")} />
      <NavButton active={screen === "today"} icon={CalendarDays} label="Dnes" onClick={() => onNavigate("today")} />
      <NavButton
        active={screen === "cases" || screen === "detail"}
        icon={BriefcaseBusiness}
        label="Prípady"
        onClick={() => onNavigate("cases")}
      />
      <NavButton active={screen === "pipeline"} icon={LayoutDashboard} label="Pipeline" onClick={() => onNavigate("pipeline")} />
      <NavButton active={screen === "settings"} icon={SettingsIcon} label="Nastavenia" onClick={() => onNavigate("settings")} />
    </nav>
  );
}

function DailyMottoStrip({ date }: { date: string }) {
  return (
    <aside className="mindset-strip" aria-label="Osobný pracovný princíp">
      <Sparkles size={14} aria-hidden="true" />
      <span>{dailyMottoForDate(date)}</span>
    </aside>
  );
}

type NowScreenProps = {
  caseItem?: RealEstateCase;
  today: string;
  onStartColdCall: () => void;
  onOpenCase: (caseId: string) => void;
  onOpenWaitingResult: (caseId: string) => void;
  onSelectOutcome: (caseId: string, outcome: CallOutcome) => void;
  onCompleteTask: (caseId: string) => void;
};

export function NowScreen({ caseItem, today, onStartColdCall, onOpenCase, onOpenWaitingResult, onSelectOutcome, onCompleteTask }: NowScreenProps) {
  const [showOutcomes, setShowOutcomes] = useState(false);
  const mottoStrip = <DailyMottoStrip date={today} />;

  if (!caseItem?.followUp) {
    return (
      <section className="screen-grid now-screen">
        {mottoStrip}
        <p className="now-empty-message">Žiadna naplánovaná aktivita.</p>
        <section className="now-action-card">
          <h2>Najlepší spôsob ako vytvoriť prácu je nový cold call.</h2>
          <button className="primary-button full-button now-primary-action" type="button" onClick={onStartColdCall}>
            <Plus size={18} aria-hidden="true" />
            Nový cold call
          </button>
        </section>
      </section>
    );
  }

  const followUp = caseItem.followUp;
  const dateLabel = followUp.date === today ? "Dnes" : formatDate(followUp.date);
  const actionKind = nextActionKind(caseItem);
  const debriefEvent = classifyDebriefEvent(caseItem, followUp);

  return (
    <section className="screen-grid now-screen">
      {mottoStrip}
      <article className="now-action-card now-task-card">
        <div className="now-task-heading">
          <div>
            <p className="eyebrow">Teraz</p>
            <h2>{caseItem.contactName}</h2>
          </div>
        </div>

        <div className="now-schedule" aria-label={`${dateLabel} o ${followUp.time}`}>
          <span>{dateLabel}</span>
          <strong>{followUp.time}</strong>
        </div>

        <div className="now-purpose">
          <span>Účel</span>
          <p>{isReservationControl(caseItem) ? RESERVATION_DUE_LABEL : followUp.purpose}</p>
        </div>

        {debriefEvent ? (
          <div className="now-meeting-context">
            <Home size={16} aria-hidden="true" />
            <span>{debriefEventLabel(debriefEvent)} · {caseItem.propertyAddress || caseItem.propertyType}</span>
          </div>
        ) : null}

        {actionKind === "call" ? (
          <a
            className="primary-button full-button now-primary-action"
            href={`tel:${caseItem.phone}`}
            onClick={() => {
              if (isWaitingCase(caseItem)) onOpenWaitingResult(caseItem.id);
              else setShowOutcomes(true);
            }}
          >
            <PhoneCall size={20} aria-hidden="true" />
            Zavolať
          </a>
        ) : debriefEvent ? (
          <button className="secondary-button full-button now-primary-action" type="button" onClick={() => onOpenCase(caseItem.id)}>
            <CalendarDays size={19} aria-hidden="true" />
            Otvoriť prípad
          </button>
        ) : listingActionId(caseItem) ? (
          <button className="primary-button full-button now-primary-action" type="button" onClick={() => onCompleteTask(caseItem.id)}>
            <CheckCircle2 size={20} aria-hidden="true" />
            {nextActionCompletionLabel(caseItem)}
          </button>
        ) : (
          <button className="primary-button full-button now-primary-action" type="button" onClick={() => onOpenCase(caseItem.id)}>
            <ClipboardList size={20} aria-hidden="true" />
            Otvoriť úlohu
          </button>
        )}
      </article>

      {showOutcomes ? (
        <div className="now-sheet-backdrop" onClick={() => setShowOutcomes(false)}>
          <section
            className="now-outcome-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="now-outcome-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="now-sheet-heading">
              <div>
                <p className="eyebrow">Výsledok hovoru</p>
                <h2 id="now-outcome-title">{caseItem.contactName}</h2>
              </div>
              <button className="icon-button" type="button" onClick={() => setShowOutcomes(false)} title="Zavrieť">
                <XCircle size={18} aria-hidden="true" />
                <span className="sr-only">Zavrieť</span>
              </button>
            </div>

            <div className="now-outcomes">
              {OUTCOMES.map((outcome) => {
                const OutcomeIcon = outcomeIcon(outcome);

                return (
                  <button
                    className="outcome-button"
                    key={outcome}
                    type="button"
                    onClick={() => onSelectOutcome(caseItem.id, outcome)}
                  >
                    <OutcomeIcon size={17} aria-hidden="true" />
                    <span>{outcome}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

type TodayScreenProps = {
  cases: RealEstateCase[];
  allCases: RealEstateCase[];
  activeCases: number;
  totalPotential: number;
  hotPotential: number;
  negotiationPotential: number;
  today: string;
  todayFilter: TodayFilter;
  todayCounts: {
    all: number;
    overdue: number;
    today: number;
    upcoming: number;
    missing: number;
  };
  onTodayFilterChange: (filter: TodayFilter) => void;
  onOpenCase: (caseId: string) => void;
  onAddNextStep: (caseId: string) => void;
  onAddFirstCase: () => void;
};

export function TodayScreen({
  cases,
  allCases,
  activeCases,
  totalPotential,
  hotPotential,
  negotiationPotential,
  today,
  todayFilter,
  todayCounts,
  onTodayFilterChange,
  onOpenCase,
  onAddNextStep,
  onAddFirstCase
}: TodayScreenProps) {
  const now = new Date();
  const firstColdCalls = allCases.filter((caseItem) => caseItem.stage === "Prvý cold call").length;
  const scheduledToday = allCases.filter((caseItem) => caseItem.followUp?.date === today);
  const todayFollowUps = scheduledToday.filter((caseItem) =>
    caseItem.stage === "Follow-up" || caseItem.stage === "Rozmýšľa"
  ).length;
  const todayViewings = scheduledToday.filter((caseItem) => caseItem.stage === "Obhliadka").length;
  const todayColdCalls = scheduledToday.filter((caseItem) =>
    caseItem.stage === "Prvý cold call" || caseItem.stage === "Nový"
  ).length;
  const nextCase = cases.find((caseItem) => caseItem.followUp) ?? cases[0];
  const nextOwnerSaleStep = nextCase && isOwnerSaleSequenceActive(nextCase)
    ? getCurrentOwnerSaleSequenceStep(nextCase)
    : undefined;
  const nextOverdue = overdueTimeLabel(nextCase?.followUp, now);
  const sectionOrder = ["Omeškané", "Dnes", "Nadchádzajúce", "Bez kroku"] as const;
  const sectionLabels = {
    Omeškané: "Po termíne",
    Dnes: "Dnes",
    Nadchádzajúce: "Nadchádzajúce",
    "Bez kroku": "Bez ďalšieho kroku"
  } as const;
  const sections = todayFilter === "Všetko"
    ? sectionOrder.map((bucket) => ({
        bucket,
        cases: cases.filter((caseItem) => nextActionBucket(caseItem, now) === bucket)
      })).filter((section) => section.cases.length > 0)
    : [{ bucket: todayFilter, cases }];

  return (
    <section className="screen-grid today-screen">
      <DailyMottoStrip date={today} />

      <section className="daily-focus-panel">
        <TodayHeroMedia label={nextCase ? propertyDisplayLabel(nextCase) : "FISPRO"} />
        <div className="daily-focus-top">
          <span>Dnes</span>
          <time dateTime={today}>{formatDate(today)}</time>
        </div>
        <div className="daily-focus-copy">
          <div>
            <strong>{todayFollowUps}</strong>
            <span>follow-upy</span>
          </div>
          <div>
            <strong>{todayViewings}</strong>
            <span>obhliadky</span>
          </div>
          <div>
            <strong>{todayColdCalls}</strong>
            <span>cold call</span>
          </div>
          <div>
            <strong>{formatCurrency(totalPotential)}</strong>
            <span>potenciál</span>
          </div>
        </div>
        <div
          className={`next-ticket ${nextCase && isMissingNextStep(nextCase) ? "missing-step actionable" : ""}`}
          role={nextCase && isMissingNextStep(nextCase) ? "button" : undefined}
          tabIndex={nextCase && isMissingNextStep(nextCase) ? 0 : undefined}
          onClick={() => { if (nextCase && isMissingNextStep(nextCase)) onAddNextStep(nextCase.id); }}
          onKeyDown={(event) => {
            if (nextCase && isMissingNextStep(nextCase) && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              onAddNextStep(nextCase.id);
            }
          }}
        >
          <div className="next-ticket-heading">
            <span>Ďalší krok</span>
            <div aria-hidden="true">
              {nextCase ? <NextStepIcon caseItem={nextCase} size={17} /> : <PhoneCall size={17} />}
            </div>
          </div>
          <strong>
            {nextCase
              ? isReservationControl(nextCase)
                ? RESERVATION_DUE_LABEL
                : nextCase.followUp?.purpose ?? nextCase.nextStep
              : "Zdvihni telefón a volaj – to jediné ti prinesie peniaze."}
          </strong>
          {nextCase ? (
            <div className="next-ticket-meta">
              <span>{nextCase.contactName}</span>
              {nextCase.followUp?.time ? <time dateTime={nextCase.followUp.time}>{nextCase.followUp.time}</time> : null}
            </div>
          ) : null}
          {nextOverdue ? <small className="overdue-note">{nextOverdue}</small> : null}
          {nextCase && !nextOwnerSaleStep ? (
            <small>{caseContextLabel(nextCase)}</small>
          ) : null}
          {nextCase && nextOwnerSaleStep ? <small>{nextCase.contactName}: {nextOwnerSaleStep.recommendedNote}</small> : null}
          {nextCase && isMissingNextStep(nextCase) ? <span className="missing-step-cta">Doplniť ďalší krok <ChevronRight size={15} aria-hidden="true" /></span> : null}
        </div>
      </section>

      <div className="hero-metrics" aria-label="Denný prehľad provízneho potenciálu">
        <Metric icon={Euro} label="Potenciál" value={formatCurrency(totalPotential)} />
        <Metric icon={Sparkles} label="HOT" value={formatCurrency(hotPotential)} />
        <Metric icon={Target} label="Rozrobené" value={formatCurrency(negotiationPotential)} />
      </div>

      <div className="filter-strip today-filter-strip" aria-label="Filter dnešnej agendy">
        {todayFilters.map((filter) => (
          <ChipButton key={filter} active={todayFilter === filter} onClick={() => onTodayFilterChange(filter)}>
            {filter}
            <span>
              {filter === "Všetko"
                ? todayCounts.all
                : filter === "Omeškané"
                  ? todayCounts.overdue
                  : filter === "Dnes"
                    ? todayCounts.today
                    : filter === "Nadchádzajúce"
                      ? todayCounts.upcoming
                      : todayCounts.missing}
            </span>
          </ChipButton>
        ))}
      </div>

      <div className="section-heading">
        <div>
          <p className="eyebrow">Agenda</p>
          <h2>Priorita podľa termínu a času</h2>
        </div>
        <span className="soft-badge">
          <PhoneCall size={14} aria-hidden="true" />
          {firstColdCalls} cold call
        </span>
      </div>

      {cases.length > 0 ? (
        <div className="today-action-sections">
          {sections.map((section) => (
            <section className={`today-action-section bucket-${section.bucket.toLocaleLowerCase("sk").replace(/\s+/g, "-")}`} key={section.bucket}>
              <div className="today-action-heading">
                <h3>{sectionLabels[section.bucket as keyof typeof sectionLabels] ?? section.bucket}</h3>
                <span>{section.cases.length}</span>
              </div>
              <div className="card-list">
                {section.cases.map((caseItem) => (
                  <CaseCard
                    key={caseItem.id}
                    caseItem={caseItem}
                    today={today}
                    compact
                    onOpenCase={onOpenCase}
                    onAddNextStep={onAddNextStep}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : allCases.length === 0 ? (
        <EmptyState
          icon={BriefcaseBusiness}
          actionLabel="Pridať prvý prípad"
          onAction={onAddFirstCase}
        />
      ) : (
        <EmptyState
          icon={CheckCircle2}
          title="Agenda je čistá"
          body="V tomto filtri nie je žiadna pracovná akcia."
        />
      )}
    </section>
  );
}

type CasesScreenProps = {
  cases: RealEstateCase[];
  query: string;
  isCreating: boolean;
  draft: NewCaseDraft;
  totalCases: number;
  caseFilter: CaseListFilter;
  transactionFilter: TransactionFilter;
  propertyFilter: PropertyFilter;
  onCaseFilterChange: (filter: CaseListFilter) => void;
  onTransactionFilterChange: (filter: TransactionFilter) => void;
  onPropertyFilterChange: (filter: PropertyFilter) => void;
  onQueryChange: (query: string) => void;
  onOpenCase: (caseId: string) => void;
  onToggleCreate: () => void;
  onAddFirstCase: () => void;
  onDraftChange: (draft: NewCaseDraft) => void;
  onCreateCase: (event: FormEvent<HTMLFormElement>) => void;
};

export function CasesScreen({
  cases,
  query,
  isCreating,
  draft,
  totalCases,
  caseFilter,
  transactionFilter,
  propertyFilter,
  onCaseFilterChange,
  onTransactionFilterChange,
  onPropertyFilterChange,
  onQueryChange,
  onOpenCase,
  onToggleCreate,
  onAddFirstCase,
  onDraftChange,
  onCreateCase
}: CasesScreenProps) {
  const archiveMode = caseFilter === "Archív";
  return (
    <section className="screen-grid cases-screen">
      <section className="cases-command">
        <div>
          <p className="eyebrow">{archiveMode ? "História" : "Tvoje prípady"}</p>
          <h2>
            {archiveMode
              ? totalCases === 1 ? "1 prípad v archíve" : `${totalCases} prípadov v archíve`
              : totalCases === 1 ? "1 pracovný prípad" : `${totalCases} pracovných prípadov`}
          </h2>
        </div>
        <span>FISPRO</span>
      </section>

      <div className="toolbar-row">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">Hľadať prípad</span>
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Kontakt, obchod, typ, lokalita..."
          />
        </label>
        <button className="primary-button square-button" type="button" onClick={onToggleCreate} title="Nový prípad">
          <Plus size={20} aria-hidden="true" />
          <span className="sr-only">Nový prípad</span>
        </button>
      </div>

      <div className="filter-strip" aria-label="Filter prípadov podľa stavu">
        {caseFilters.map((filter) => (
          <ChipButton key={filter} active={caseFilter === filter} onClick={() => onCaseFilterChange(filter)}>
            {filter}
          </ChipButton>
        ))}
      </div>

      <div className="filter-select-row">
        <SelectField
          label="Typ obchodu"
          value={transactionFilter}
          onChange={(value) => onTransactionFilterChange(value as TransactionFilter)}
          options={["Všetky obchody", ...TRANSACTION_TYPES]}
        />
        <SelectField
          label="Typ nehnuteľnosti"
          value={propertyFilter}
          onChange={(value) => onPropertyFilterChange(value as PropertyFilter)}
          options={["Všetky typy", ...PROPERTY_TYPES]}
        />
      </div>

      {isCreating && (
        <form className="creation-panel" onSubmit={onCreateCase}>
          <PanelHeader eyebrow="Nový prípad" title="Prvý cold call ako štart workflowu" icon={PhoneCall} />

          <div className="form-grid">
            <TextField
              label="Meno kontaktu"
              value={draft.contactName}
              onChange={(contactName) => onDraftChange({ ...draft, contactName })}
              required
            />
            <TextField
              label="Telefón"
              value={draft.phone}
              onChange={(phone) => onDraftChange({ ...draft, phone })}
              required
              inputMode="tel"
            />
            <TextField
              label="E-mail"
              value={draft.email}
              onChange={(email) => onDraftChange({ ...draft, email })}
              type="email"
            />
            <SelectField
              label="Typ obchodu"
              value={draft.transactionType}
              onChange={(transactionType) => onDraftChange({ ...draft, transactionType: transactionType as TransactionType })}
              options={TRANSACTION_TYPES}
              required
            />
            <SelectField
              label="Typ nehnuteľnosti"
              value={draft.propertyType}
              onChange={(propertyType) =>
                onDraftChange({
                  ...draft,
                  propertyType: propertyType as PropertyType,
                  apartmentRooms: propertyType === "Byt" ? draft.apartmentRooms : "2-izbový"
                })
              }
              options={PROPERTY_TYPES}
              required
            />
            {draft.propertyType === "Byt" ? (
              <SelectField
                label="Počet izieb"
                value={draft.apartmentRooms}
                onChange={(apartmentRooms) => onDraftChange({ ...draft, apartmentRooms: apartmentRooms as ApartmentRooms })}
                options={APARTMENT_ROOM_OPTIONS}
                required
              />
            ) : null}
            <CooperationSelect
              value={draft.cooperationType}
              onChange={(cooperationType) => onDraftChange({ ...draft, cooperationType })}
            />
            <TextField
              label="Výmera"
              value={draft.area}
              onChange={(area) => onDraftChange({ ...draft, area })}
              inputMode="numeric"
              type="number"
              placeholder="m²"
            />
          </div>

          <CaseLocationFields value={draft} onChange={onDraftChange} cityRequired />

          <div className="form-grid">
            <TextField
              label="Cena na inzeráte"
              value={draft.askingPrice}
              onChange={(askingPrice) => onDraftChange({ ...draft, askingPrice })}
              inputMode="numeric"
              type="number"
            />
            <TextField
              label="Moja odporúčaná cena"
              value={draft.recommendedPrice}
              onChange={(recommendedPrice) => onDraftChange({ ...draft, recommendedPrice })}
              inputMode="numeric"
              type="number"
            />
            <TextField
              label="Potenciál provízie"
              value={draft.commissionPotential}
              onChange={(commissionPotential) => onDraftChange({ ...draft, commissionPotential })}
              inputMode="numeric"
              type="number"
              placeholder="Ak necháš prázdne, počíta sa 3 %"
            />
          </div>

          <div className="followup-panel">
            <p className="micro-heading">Presný follow-up</p>
            <div className="form-grid three-columns">
              <TextField
                label="Dátum"
                value={draft.followUpDate}
                onChange={(followUpDate) => onDraftChange({ ...draft, followUpDate })}
                type="date"
                required
              />
              <TextField
                label="Čas"
                value={draft.followUpTime}
                onChange={(followUpTime) => onDraftChange({ ...draft, followUpTime })}
                type="time"
                required
              />
              <TextField
                label="Účel"
                value={draft.followUpPurpose}
                onChange={(followUpPurpose) => onDraftChange({ ...draft, followUpPurpose })}
                required
              />
            </div>
          </div>

          <label className="text-area-field">
            <span>Poznámky</span>
            <textarea
              value={draft.notes}
              onChange={(event) => onDraftChange({ ...draft, notes: event.target.value })}
              rows={4}
              placeholder="Kontext, motivácia, námietky..."
            />
          </label>

          <button className="primary-button full-button" type="submit">
            <Plus size={18} aria-hidden="true" />
            Vytvoriť prípad
          </button>
        </form>
      )}

      {cases.length > 0 ? (
        <div className="card-list">
          {cases.map((caseItem) => (
            <CaseCard
              key={caseItem.id}
              caseItem={caseItem}
              today={undefined}
              onOpenCase={onOpenCase}
            />
          ))}
        </div>
      ) : totalCases === 0 && !isCreating && archiveMode ? (
        <EmptyState icon={CheckCircle2} title="Archív je prázdny" body="Vyradené a dokončené prípady sa zobrazia tu." />
      ) : totalCases === 0 && !isCreating ? (
        <EmptyState
          icon={BriefcaseBusiness}
          title="Prvý prípad ešte čaká"
          body="Pridaj kontakt, nehnuteľnosť a presný čas prvého cold callu."
          actionLabel="Pridať prvý prípad"
          onAction={onAddFirstCase}
        />
      ) : !isCreating ? (
        <EmptyState icon={Search} title="Nenašli sa prípady" body="Skús zmeniť vyhľadávanie, typ obchodu alebo typ nehnuteľnosti." />
      ) : null}
    </section>
  );
}

type CaseDetailScreenProps = {
  caseItem: RealEstateCase;
  outcomeDraft: OutcomeDraft | null;
  outcomeConfig: OutcomeConfigMap;
  today: string;
  onBeginOutcome: (outcome: CallOutcome) => void;
  onOutcomeDraftChange: (draft: OutcomeDraft | null) => void;
  onSaveOutcome: () => void;
  onWaitAfterRejection: (caseId: string) => void;
  onWaitingOutcome: (caseId: string, outcome: Exclude<WaitingOutcome, "Definitívne vyradiť">, draft: NextStepDraft) => void;
  onNotesChange: (caseId: string, notes: string) => void;
  onCaseChange: (caseId: string, draft: CaseEditDraft) => void;
  onStageChange: (caseId: string, stage: CaseStage, reservationUntil?: string) => void;
  onReservationOutcome: (
    caseId: string,
    outcome: ReservationOutcome,
    reservationUntil?: string,
    nextStep?: NextStepDraft
  ) => void;
  onFollowUpChange: (caseId: string, draft: NextStepDraft) => void;
  onFollowUpComplete: (caseId: string) => FollowUpCompletion | null;
  onFollowUpCompleteWithNext: (caseId: string, draft: NextStepDraft) => FollowUpCompletion | null;
  onOwnerSaleContactComplete: (caseId: string, draft: OwnerSaleContactDraft) => void;
  onOwnerSaleSequenceEnd: (caseId: string, draft: OwnerSaleEndDraft) => void;
  onTogglePriority: (caseId: string) => void;
  onCloseCase: (caseId: string, draft: ClosingDraft) => void;
  onSellCase: (caseId: string, draft: SoldDraft) => void;
  onRejectCase: (caseId: string, draft: RejectionDraft) => void;
  onDeleteCase: (caseId: string) => void;
  onRestoreCase: (caseId: string) => void;
  onShotScoreSave: (caseId: string, draft: ShotScoreDraft) => void;
  onMarketStatsSave: (caseId: string, input: MarketStatsInput) => void;
  pendingRequiredDebrief?: DebriefReminder;
  now: Date;
  onOpenDebrief: (caseId: string, reminderId: string) => void;
  onSnoozeDebrief: (caseId: string, reminderId: string, minutes: number) => void;
  openNextStepOnMount: boolean;
  openWaitingResultOnMount: boolean;
  onNextStepIntentHandled: () => void;
};

export function CaseDetailScreen({
  caseItem,
  outcomeDraft,
  outcomeConfig,
  today,
  onBeginOutcome,
  onOutcomeDraftChange,
  onSaveOutcome,
  onWaitAfterRejection,
  onWaitingOutcome,
  onNotesChange,
  onCaseChange,
  onStageChange,
  onReservationOutcome,
  onFollowUpChange,
  onFollowUpComplete,
  onFollowUpCompleteWithNext,
  onOwnerSaleContactComplete,
  onOwnerSaleSequenceEnd,
  onTogglePriority,
  onCloseCase,
  onSellCase,
  onRejectCase,
  onDeleteCase,
  onRestoreCase,
  onShotScoreSave,
  onMarketStatsSave,
  pendingRequiredDebrief,
  now,
  onOpenDebrief,
  onSnoozeDebrief,
  openNextStepOnMount,
  openWaitingResultOnMount,
  onNextStepIntentHandled
}: CaseDetailScreenProps) {
  const initialManualStep = nextManualStepDraft();
  const { analyses: marketAnalyses } = useMarketAnalysisContext();
  const activeOutcomeConfig = outcomeDraft ? outcomeConfig[outcomeDraft.outcome] : null;
  const ownerSaleActive = isOwnerSaleSequenceActive(caseItem);
  const ownerSaleEnded = isOwnerSaleSequenceEnded(caseItem);
  const ownerSaleStep = getCurrentOwnerSaleSequenceStep(caseItem);
  const nextOwnerSaleStep = getNextOwnerSaleSequenceStep(ownerSaleStep?.step);
  const canSaveOutcome =
    !outcomeDraft ||
    !activeOutcomeConfig?.requiresFollowUp ||
    Boolean(outcomeDraft.date && outcomeDraft.time && outcomeDraft.purpose.trim());
  const nextStepPanelRef = useRef<HTMLElement>(null);
  const [actionUiState, setActionUiState] = useState<NextActionUiState>("READY");
  const [showColdCallClosingReminder, setShowColdCallClosingReminder] = useState(false);
  const [nextStepDraft, setNextStepDraft] = useState<NextStepDraft>({
    date: caseItem.followUp?.date ?? initialManualStep.date,
    time: caseItem.followUp?.time ?? initialManualStep.time,
    endTime: caseItem.followUp?.endTime,
    purpose: caseItem.followUp?.purpose ?? "Doplniť ďalší krok"
  });
  const [ownerSaleContactDraft, setOwnerSaleContactDraft] = useState<OwnerSaleContactDraft>(() =>
    createOwnerSaleContactDraft(caseItem)
  );
  const [ownerSaleEndDraft, setOwnerSaleEndDraft] = useState<OwnerSaleEndDraft>({
    reason: caseItem.ownerSaleSequence?.endReason ?? "Sekvencia ukončená manuálne",
    note: caseItem.ownerSaleSequence?.lastNote ?? ""
  });
  const [closingDraft, setClosingDraft] = useState<ClosingDraft>({
    closedAt: caseItem.closedAt ?? today,
    actualCommission: caseItem.actualCommission ? String(caseItem.actualCommission) : "",
    note: caseItem.closingNote ?? ""
  });
  const [rejectionDraft, setRejectionDraft] = useState<RejectionDraft>({
    reason: caseItem.lostReason ?? ""
  });
  const [soldDraft, setSoldDraft] = useState<SoldDraft>({
    realizedPrice: caseItem.realizedPrice ? String(caseItem.realizedPrice) : "",
    soldAt: caseItem.soldAt?.slice(0, 10) ?? today
  });
  const [isSelling, setIsSelling] = useState(false);
  const [isReserving, setIsReserving] = useState(false);
  const [reservationUntilDraft, setReservationUntilDraft] = useState(caseItem.reservationUntil ?? "");
  const [reservationResultMode, setReservationResultMode] = useState<"select" | "deal" | "extend" | "continues" | null>(null);
  const [pendingShotScore, setPendingShotScore] = useState<ShotScoreDraft | null>(null);
  const [isEditingCase, setIsEditingCase] = useState(false);
  const [showCaseMenu, setShowCaseMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmExclusion, setConfirmExclusion] = useState(false);
  const [waitingOutcome, setWaitingOutcome] = useState<WaitingOutcome | null>(null);
  const [editDraft, setEditDraft] = useState<CaseEditDraft>(() => caseEditDraftFromCase(caseItem));

  useEffect(() => {
    if (!outcomeDraft) return;
    setActionUiState(outcomeNextUiState(outcomeDraft.outcome, outcomeConfig));
    window.requestAnimationFrame(() => nextStepPanelRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }));
  }, [outcomeConfig, outcomeDraft?.outcome]);

  useEffect(() => {
    const manualStep = nextManualStepDraft();
    setNextStepDraft({
      date: caseItem.followUp?.date ?? manualStep.date,
      time: caseItem.followUp?.time ?? manualStep.time,
      endTime: caseItem.followUp?.endTime,
      purpose: caseItem.followUp?.purpose ?? "Doplniť ďalší krok"
    });
    setClosingDraft({
      closedAt: caseItem.closedAt ?? today,
      actualCommission: caseItem.actualCommission ? String(caseItem.actualCommission) : "",
      note: caseItem.closingNote ?? ""
    });
    setRejectionDraft({ reason: caseItem.lostReason ?? "" });
    setOwnerSaleContactDraft(createOwnerSaleContactDraft(caseItem));
    setOwnerSaleEndDraft({
      reason: caseItem.ownerSaleSequence?.endReason ?? "Sekvencia ukončená manuálne",
      note: caseItem.ownerSaleSequence?.lastNote ?? ""
    });
    setSoldDraft({
      realizedPrice: caseItem.realizedPrice ? String(caseItem.realizedPrice) : "",
      soldAt: caseItem.soldAt?.slice(0, 10) ?? today
    });
    setIsSelling(false);
    setIsReserving(false);
    setReservationUntilDraft(caseItem.reservationUntil ?? "");
    setReservationResultMode(null);
    setEditDraft(caseEditDraftFromCase(caseItem));
    setIsEditingCase(false);
    setShowCaseMenu(false);
    setConfirmDelete(false);
    setConfirmExclusion(false);
    setWaitingOutcome(null);
    if (!outcomeDraft) setActionUiState("READY");
  }, [
    caseItem.id,
    caseItem.followUp,
    caseItem.ownerSaleSequence,
    caseItem.closedAt,
    caseItem.actualCommission,
    caseItem.closingNote,
    caseItem.lostReason,
    caseItem.propertyAddress,
    caseItem.city,
    caseItem.cityNormalized,
    caseItem.citySource,
    caseItem.municipalityId,
    caseItem.street,
    caseItem.streetNormalized,
    caseItem.streetSource,
    caseItem.streetId,
    caseItem.locality,
    caseItem.area,
    caseItem.contactName,
    caseItem.phone,
    caseItem.email,
    caseItem.transactionType,
    caseItem.propertyType,
    caseItem.apartmentRooms,
    caseItem.cooperationType,
    caseItem.askingPrice,
    caseItem.recommendedPrice,
    caseItem.commissionPotential,
    caseItem.notes,
    caseItem.realizedPrice,
    caseItem.soldAt,
    caseItem.reservationUntil,
    today
  ]);

  useEffect(() => {
    if (!openNextStepOnMount) {
      return;
    }
    if (pendingRequiredDebrief) {
      onOpenDebrief(caseItem.id, pendingRequiredDebrief.id);
      onNextStepIntentHandled();
      return;
    }
    if (terminalStages.includes(caseItem.stage) || isAgendaExcludedCase(caseItem)) {
      onNextStepIntentHandled();
      return;
    }
    if (caseItem.stage === "Rezervované" && !isReservationControl(caseItem)) {
      setReservationUntilDraft(caseItem.reservationUntil ?? "");
      setIsReserving(true);
      window.requestAnimationFrame(() => nextStepPanelRef.current?.scrollIntoView({ block: "start", behavior: "auto" }));
      onNextStepIntentHandled();
      return;
    }
    if (isReservationControl(caseItem)) {
      setReservationResultMode("select");
      window.requestAnimationFrame(() => nextStepPanelRef.current?.scrollIntoView({ block: "start", behavior: "auto" }));
      onNextStepIntentHandled();
      return;
    }
    if (!caseItem.followUp && ownerSaleActive) setNextStepDraft(createOwnerSalePlanDraft(caseItem, today));
    setActionUiState("RESCHEDULING");
    window.requestAnimationFrame(() => nextStepPanelRef.current?.scrollIntoView({ block: "start", behavior: "auto" }));
    onNextStepIntentHandled();
  }, [caseItem, onNextStepIntentHandled, onOpenDebrief, openNextStepOnMount, ownerSaleActive, pendingRequiredDebrief, today]);

  useEffect(() => {
    if (pendingRequiredDebrief) setActionUiState("READY");
  }, [pendingRequiredDebrief?.id]);

  useEffect(() => {
    if (!openWaitingResultOnMount || !isWaitingCase(caseItem)) return;
    if (pendingRequiredDebrief) {
      onOpenDebrief(caseItem.id, pendingRequiredDebrief.id);
      onNextStepIntentHandled();
      return;
    }
    setActionUiState("WAITING_RESULT");
    window.requestAnimationFrame(() => nextStepPanelRef.current?.scrollIntoView({ block: "start", behavior: "auto" }));
    onNextStepIntentHandled();
  }, [caseItem, onNextStepIntentHandled, onOpenDebrief, openWaitingResultOnMount, pendingRequiredDebrief]);

  const terminalCase = terminalStages.includes(caseItem.stage) || isClosedLostCase(caseItem);
  const waitingCase = isWaitingCase(caseItem);
  const excludedCase = isExcludedCase(caseItem);
  const missingStep = isMissingNextStep(caseItem);
  const actionKind = nextActionKind(caseItem);
  const isCallAction = actionKind === "call";
  const isMeetingAction = Boolean(caseItem.followUp && classifyDebriefEvent(caseItem, caseItem.followUp));
  const canMarkAsListed = canStartListingWorkflow(caseItem) && !caseItem.followUp;
  const reservationControl = isReservationControl(caseItem);
  const reservationNeedsDate = caseItem.stage === "Rezervované" && !reservationControl;
  const shouldShowColdCallClosingReminder = caseItem.stage === "Prvý cold call";
  const shouldShowColdCallClosingReminderAfterSave =
    shouldShowColdCallClosingReminder &&
    Boolean(outcomeDraft && activeOutcomeConfig?.requiresFollowUp && canSaveOutcome);
  const canSaveOwnerSaleContact =
    Boolean(ownerSaleContactDraft.result.trim() && ownerSaleContactDraft.note.trim()) &&
    (!nextOwnerSaleStep ||
      Boolean(
        ownerSaleContactDraft.nextDate &&
          ownerSaleContactDraft.nextTime &&
          ownerSaleContactDraft.nextPurpose.trim()
      ));
  const timelineActivities = useMemo(
    () => [...caseItem.timeline, ...marketAnalysisTimelineActivities(marketAnalyses, caseItem.id)]
      .sort((first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt)),
    [caseItem.id, caseItem.timeline, marketAnalyses]
  );
  const missingShotScore = caseItem.timeline.find(
    (activity) =>
      (activity.activityKind === "meeting" || activity.activityKind === "viewing") &&
      !(caseItem.shotScores ?? []).some((score) => score.activityId === activity.id)
  );

  return (
    <section className="detail-layout">
      <section className={`identity-panel ${caseItem.priority === "HOT" ? "hot-panel" : ""}`}>
        <PropertyMedia
          className="identity-property-media"
          propertyType={caseItem.propertyType}
          seed={caseItem.id}
          imageUrl={caseImageUrl(caseItem)}
          label={propertyDisplayLabel(caseItem)}
          eager
        />
        <div className="contact-monogram" aria-hidden="true">
          {caseItem.contactName.trim().charAt(0) || "F"}
        </div>
        <div className="identity-content">
          <p className="eyebrow">Kontakt</p>
          <h2>{caseItem.contactName}</h2>
          <p>{caseContextLabel(caseItem)} / {caseLocationLabel(caseItem)}</p>
        </div>
        <button className={`hot-toggle ${caseItem.priority === "HOT" ? "active" : ""}`} type="button" onClick={() => onTogglePriority(caseItem.id)}>
          HOT
        </button>
        <div className="case-menu-wrap">
          <button
            className="icon-button case-menu-trigger"
            type="button"
            aria-label="Možnosti prípadu"
            aria-expanded={showCaseMenu}
            onClick={() => setShowCaseMenu((current) => !current)}
          >
            <MoreHorizontal size={20} aria-hidden="true" />
          </button>
          {showCaseMenu ? (
            <div className="case-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => { setIsEditingCase(true); setShowCaseMenu(false); }}>
                <Pencil size={16} aria-hidden="true" /> Upraviť prípad
              </button>
              {!excludedCase && !pendingRequiredDebrief ? (
                <button type="button" role="menuitem" onClick={() => { setConfirmExclusion(true); setShowCaseMenu(false); }}>
                  <XCircle size={16} aria-hidden="true" /> Vyradiť prípad
                </button>
              ) : null}
              <button className="danger-menu-item" type="button" role="menuitem" onClick={() => { setConfirmDelete(true); setShowCaseMenu(false); }}>
                <Trash2 size={16} aria-hidden="true" /> Odstrániť prípad
              </button>
            </div>
          ) : null}
        </div>
        {!excludedCase ? <div className="quick-actions" aria-label="Rýchle akcie kontaktu">
          {!isCallAction || !caseItem.followUp ? (
            <a className="quick-action primary" href={`tel:${caseItem.phone}`}>
              <PhoneCall size={17} aria-hidden="true" />
              Zavolať
            </a>
          ) : null}
          <a className="quick-action" href={`sms:${caseItem.phone}`}>
            <MessageSquare size={17} aria-hidden="true" />
            SMS
          </a>
          {caseItem.email ? (
            <a className="quick-action" href={`mailto:${caseItem.email}`}>
              <Mail size={17} aria-hidden="true" />
              E-mail
            </a>
          ) : null}
        </div> : null}
      </section>

      {confirmDelete || confirmExclusion ? (
        <div className="call-reminder-backdrop" role="presentation">
          <section className="case-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="case-confirm-title">
            <div>
              <p className="eyebrow">{confirmDelete ? "Administratívna operácia" : "Obchodný stav"}</p>
              <h2 id="case-confirm-title">
                {confirmDelete
                  ? `Naozaj chceš odstrániť prípad ${caseItem.contactName}?`
                  : "Definitívne vyradiť tento prípad?"}
              </h2>
            </div>
            {confirmExclusion ? (
              <TextField
                label="Dôvod vyradenia"
                value={rejectionDraft.reason}
                onChange={(reason) => setRejectionDraft({ reason })}
                placeholder="Napr. klient nechce ďalší kontakt"
              />
            ) : (
              <p>Odstránenie je určené iba pre omyl, testovací prípad alebo duplicitu. Prípad nebude v štatistikách.</p>
            )}
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => { setConfirmDelete(false); setConfirmExclusion(false); }}>Zrušiť</button>
              <button
                className="danger-button"
                type="button"
                onClick={() => {
                  if (confirmDelete) onDeleteCase(caseItem.id);
                  else onRejectCase(caseItem.id, rejectionDraft);
                  setConfirmDelete(false);
                  setConfirmExclusion(false);
                }}
              >
                {confirmDelete ? "Odstrániť" : "Vyradiť prípad"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {showColdCallClosingReminder ? (
        <div className="call-reminder-backdrop" role="presentation">
          <section
            className="call-reminder-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cold-call-reminder-title"
          >
            <div className="call-reminder-icon" aria-hidden="true">
              <PhoneCall size={20} />
            </div>
            <div>
              <p className="eyebrow">Po cold calle</p>
              <h2 id="cold-call-reminder-title">Ako zakončiť rozhovor</h2>
            </div>
            <blockquote>{coldCallClosingReminder}</blockquote>
            <button className="primary-button full-button" type="button" onClick={() => setShowColdCallClosingReminder(false)}>
              Rozumiem
            </button>
          </section>
        </div>
      ) : null}

      {pendingRequiredDebrief ? (
        <DebriefGateCard
          caseItem={caseItem}
          reminder={pendingRequiredDebrief}
          now={now}
          detail
          onStart={onOpenDebrief}
          onSnooze={onSnoozeDebrief}
          onNotRequired={onOpenDebrief}
        />
      ) : null}

      {!excludedCase && !pendingRequiredDebrief ? <section ref={nextStepPanelRef} className={`next-step-panel ${missingStep ? "warning-panel" : ""}`}>
        <div className="panel-title-row">
          <div>
            <p className="eyebrow">Ďalší krok</p>
            <h2>{reservationControl
              ? "Kontrola rezervácie"
              : reservationNeedsDate
                ? "Doplniť dátum rezervácie"
                : caseItem.followUp?.purpose ?? caseItem.nextStep}</h2>
            {caseItem.followUp ? (
              <time className="next-step-due" dateTime={`${caseItem.followUp.date}T${caseItem.followUp.time}`}>
                {reservationControl
                  ? `Rezervované do ${formatDate(caseItem.reservationUntil ?? caseItem.followUp.date)} · ${caseItem.followUp.time}`
                  : formatDateTime(caseItem.followUp)}
              </time>
            ) : null}
          </div>
          <NextStepIcon caseItem={caseItem} size={22} />
        </div>
        <div className="next-step-meta">
          <span className="stage-chip">{caseItem.stage}</span>
          <strong>{formatCurrency(caseItem.commissionPotential)}</strong>
        </div>
        {!terminalCase && actionUiState === "READY" ? (
          <div className="button-row compact-actions next-action-controls">
            {canMarkAsListed ? (
              <button className="primary-button" type="button" onClick={() => onStageChange(caseItem.id, "Zainzerované")}>
                <Building2 size={17} aria-hidden="true" />
                Označiť ako Zainzerované
              </button>
            ) : caseItem.followUp && isCallAction ? (
              <a
                className="primary-button"
                href={`tel:${caseItem.phone}`}
                onClick={() => setActionUiState(waitingCase ? "WAITING_RESULT" : ownerSaleActive ? "OWNER_RESULT" : "CALL_RESULT")}
              >
                <PhoneCall size={17} aria-hidden="true" />
                Zavolať
              </a>
            ) : reservationControl ? (
              <button
                className="primary-button"
                type="button"
                onClick={() => setReservationResultMode("select")}
              >
                <CalendarCheck size={17} aria-hidden="true" />
                Vyhodnotiť rezerváciu
              </button>
            ) : caseItem.followUp && !isMeetingAction ? (
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  if (!listingActionId(caseItem)) {
                    setNextStepDraft({
                      date: initialManualStep.date,
                      time: caseItem.followUp?.time ?? initialManualStep.time,
                      purpose: "Doplniť ďalší krok"
                    });
                    setActionUiState("COMPLETION_SCHEDULING");
                    return;
                  }
                  const completion = onFollowUpComplete(caseItem.id);
                  if (completion?.activityType) {
                    setPendingShotScore({
                      activityId: completion.activityId,
                      activityType: completion.activityType,
                      completedAt: completion.completedAt,
                      identity: false,
                      value: false,
                      acquisition: false,
                      nextStep: false
                    });
                  }
                }}
              >
                <CalendarCheck size={17} aria-hidden="true" />
                {nextActionCompletionLabel(caseItem)}
              </button>
            ) : null}
            {!canMarkAsListed ? <button
              className="secondary-button"
              type="button"
              onClick={() => {
                if (reservationNeedsDate) {
                  setIsReserving(true);
                  return;
                }
                if (ownerSaleActive) setNextStepDraft(createOwnerSalePlanDraft(caseItem, today));
                setActionUiState("RESCHEDULING");
              }}
            >
              {reservationNeedsDate ? "Nastaviť dátum" : caseItem.followUp ? "Preložiť" : "Doplniť krok"}
            </button> : null}
          </div>
        ) : null}

        {isReserving && reservationNeedsDate ? (
          <div className="terminal-panel">
            <TextField label="Rezervované do" type="date" min={today} value={reservationUntilDraft} onChange={setReservationUntilDraft} required />
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => setIsReserving(false)}>Zrušiť</button>
              <button
                className="primary-button"
                type="button"
                disabled={!reservationUntilDraft || reservationUntilDraft < today}
                onClick={() => { onStageChange(caseItem.id, "Rezervované", reservationUntilDraft); setIsReserving(false); }}
              >
                Potvrdiť rezerváciu
              </button>
            </div>
          </div>
        ) : null}

        {reservationResultMode === "select" && reservationControl ? (
          <div className="call-result-flow">
            <div className="progressive-heading">
              <p className="eyebrow">Výsledok rezervácie</p>
              <h3>Čo sa stalo po skončení rezervácie?</h3>
            </div>
            <div className="outcome-grid">
              <button className="outcome-button" type="button" onClick={() => setReservationResultMode("deal")}>
                <CheckCircle2 size={17} aria-hidden="true" /><span>DEAL – obchod dokončený</span>
              </button>
              <button className="outcome-button" type="button" onClick={() => { onReservationOutcome(caseItem.id, "fee_returned"); setReservationResultMode(null); }}>
                <CalendarCheck size={17} aria-hidden="true" /><span>Rezervačný poplatok vrátený</span>
              </button>
              <button className="outcome-button" type="button" onClick={() => { onReservationOutcome(caseItem.id, "fee_settled"); setReservationResultMode(null); }}>
                <CalendarCheck size={17} aria-hidden="true" /><span>Rezervačný poplatok prepadol / bol vysporiadaný</span>
              </button>
              <button className="outcome-button" type="button" onClick={() => { setReservationUntilDraft(""); setReservationResultMode("extend"); }}>
                <CalendarDays size={17} aria-hidden="true" /><span>Rezervácia predĺžená</span>
              </button>
              <button className="outcome-button" type="button" onClick={() => {
                setNextStepDraft({ ...nextStepDraft, purpose: "Pokračovať v obchode po rezervácii" });
                setReservationResultMode("continues");
              }}>
                <ChevronRight size={17} aria-hidden="true" /><span>Obchod pokračuje – nový termín</span>
              </button>
            </div>
            <button className="text-button" type="button" onClick={() => setReservationResultMode(null)}>Zrušiť</button>
          </div>
        ) : null}

        {reservationResultMode === "deal" && reservationControl ? (
          <div className="terminal-panel">
            <div className="progressive-heading">
              <p className="eyebrow">DEAL</p>
              <h3>Dokončiť obchod</h3>
            </div>
            <div className="form-grid">
              <TextField
                label="Realizovaná cena"
                type="number"
                inputMode="numeric"
                value={soldDraft.realizedPrice}
                onChange={(realizedPrice) => setSoldDraft({ ...soldDraft, realizedPrice })}
                required
              />
              <TextField
                label="Dátum predaja"
                type="date"
                value={soldDraft.soldAt}
                onChange={(soldAt) => setSoldDraft({ ...soldDraft, soldAt })}
                required
              />
            </div>
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => setReservationResultMode("select")}>Späť</button>
              <button
                className="primary-button"
                type="button"
                disabled={!soldDraft.soldAt || Number(soldDraft.realizedPrice) <= 0}
                onClick={() => { onSellCase(caseItem.id, soldDraft); setReservationResultMode(null); }}
              >
                <CheckCircle2 size={17} aria-hidden="true" />
                Uložiť DEAL
              </button>
            </div>
          </div>
        ) : null}

        {reservationResultMode === "extend" && reservationControl ? (
          <div className="terminal-panel">
            <TextField
              label="Rezervované do"
              type="date"
              min={today}
              value={reservationUntilDraft}
              onChange={setReservationUntilDraft}
              required
            />
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => setReservationResultMode("select")}>Späť</button>
              <button
                className="primary-button"
                type="button"
                disabled={!reservationUntilDraft || reservationUntilDraft < today}
                onClick={() => { onReservationOutcome(caseItem.id, "extended", reservationUntilDraft); setReservationResultMode(null); }}
              >
                Predĺžiť rezerváciu
              </button>
            </div>
          </div>
        ) : null}

        {reservationResultMode === "continues" && reservationControl ? (
          <NextActionScheduler
            today={today}
            value={nextStepDraft}
            onChange={setNextStepDraft}
            onCancel={() => setReservationResultMode("select")}
            onSave={() => { onReservationOutcome(caseItem.id, "continues", undefined, nextStepDraft); setReservationResultMode(null); }}
            saveLabel="Uložiť nový termín"
          />
        ) : null}

        {actionUiState === "RESCHEDULING" && !terminalCase ? (
          <NextActionScheduler
            today={today}
            value={nextStepDraft}
            onChange={setNextStepDraft}
            onCancel={() => setActionUiState("READY")}
            onSave={() => {
              onFollowUpChange(caseItem.id, nextStepDraft);
              setActionUiState("READY");
            }}
            saveLabel={caseItem.followUp ? "Uložiť nový termín" : "Uložiť ďalší krok"}
          />
        ) : null}

        {actionUiState === "COMPLETION_SCHEDULING" && caseItem.followUp && !terminalCase ? (
          <div className="completion-scheduling-flow">
            <div className="progressive-heading">
              <p className="eyebrow">Po dokončení</p>
              <h3>Čo bude nasledovať?</h3>
            </div>
            <NextActionScheduler
              today={today}
              value={nextStepDraft}
              onChange={setNextStepDraft}
              onCancel={() => setActionUiState("READY")}
              onSave={() => {
                const completion = onFollowUpCompleteWithNext(caseItem.id, nextStepDraft);
                if (completion?.activityType) {
                  setPendingShotScore({
                    activityId: completion.activityId,
                    activityType: completion.activityType,
                    completedAt: completion.completedAt,
                    identity: false,
                    value: false,
                    acquisition: false,
                    nextStep: false
                  });
                }
                setActionUiState("READY");
              }}
              saveLabel="Dokončiť a naplánovať"
            />
          </div>
        ) : null}

        {actionUiState === "CALL_RESULT" && !terminalCase ? (
          <div className="call-result-flow">
            <div className="progressive-heading">
              <p className="eyebrow">Po hovore</p>
              <h3>Ako dopadol hovor?</h3>
            </div>
            <div className="outcome-grid">
              {OUTCOMES.map((outcome) => {
                const OutcomeIcon = outcomeIcon(outcome);
                return (
                  <button
                    className={`outcome-button ${outcomeDraft?.outcome === outcome ? "selected" : ""}`}
                    key={outcome}
                    type="button"
                    onClick={() => {
                      onBeginOutcome(outcome);
                      setActionUiState(outcomeNextUiState(outcome, outcomeConfig));
                    }}
                  >
                    <OutcomeIcon size={17} aria-hidden="true" />
                    <span>{outcome}</span>
                  </button>
                );
              })}
            </div>
            {outcomeDraft && !activeOutcomeConfig?.requiresFollowUp ? (
              <div className="outcome-confirmation">
                {outcomeDraft.outcome === "Nezáujem" ? (
                  <>
                    <h3>Presunúť medzi čakajúce a ozvať sa o 30 dní?</h3>
                    <p>FISPRO vytvorí jeden follow-up „Overiť situáciu klienta“ a použije doterajší čas hovoru.</p>
                    <button className="primary-button full-button" type="button" onClick={() => { onWaitAfterRejection(caseItem.id); setActionUiState("READY"); }}>
                      Áno, pripomenúť o 30 dní
                    </button>
                    <button className="secondary-button full-button" type="button" onClick={() => { onRejectCase(caseItem.id, { reason: "Nezáujem - klient nechce ďalší kontakt" }); onOutcomeDraftChange(null); setActionUiState("READY"); }}>
                      Definitívne vyradiť
                    </button>
                    <button className="text-button" type="button" onClick={() => { onOutcomeDraftChange(null); setActionUiState("READY"); }}>Zrušiť</button>
                  </>
                ) : (
                  <div className="button-row">
                    <button className="secondary-button" type="button" onClick={() => { onOutcomeDraftChange(null); setActionUiState("READY"); }}>Zrušiť</button>
                    <button className="primary-button" type="button" onClick={() => { onSaveOutcome(); setActionUiState("READY"); }}>Uložiť výsledok</button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {actionUiState === "WAITING_RESULT" && waitingCase ? (
          <div className="call-result-flow">
            <div className="progressive-heading">
              <p className="eyebrow">Po hovore</p>
              <h3>Ako sa zmenila situácia klienta?</h3>
            </div>
            <div className="outcome-grid waiting-outcomes">
              {(["Má záujem", "Ozvať sa neskôr", "Stále odmieta", "Definitívne vyradiť"] as WaitingOutcome[]).map((outcome) => (
                <button
                  className="outcome-button"
                  key={outcome}
                  type="button"
                  onClick={() => {
                    setWaitingOutcome(outcome);
                    if (outcome === "Definitívne vyradiť") return;
                    const days = outcome === "Stále odmieta" ? 90 : outcome === "Ozvať sa neskôr" ? 30 : 1;
                    setNextStepDraft({
                      date: datePlusDays(today, days),
                      time: caseItem.followUp?.time ?? initialManualStep.time,
                      purpose: outcome === "Má záujem"
                        ? "Dohodnúť ďalší postup s klientom"
                        : "Overiť situáciu klienta"
                    });
                    setActionUiState("WAITING_SCHEDULING");
                  }}
                >
                  {outcome}
                </button>
              ))}
            </div>
            {waitingOutcome === "Definitívne vyradiť" ? (
              <div className="outcome-confirmation">
                <p>Prípad zostane v histórii, ale ďalší reminder sa už nevytvorí.</p>
                <div className="button-row">
                  <button className="secondary-button" type="button" onClick={() => setWaitingOutcome(null)}>Späť</button>
                  <button className="danger-button" type="button" onClick={() => { onRejectCase(caseItem.id, { reason: "Klient definitívne odmieta ďalší kontakt" }); setWaitingOutcome(null); setActionUiState("READY"); }}>
                    Definitívne vyradiť
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {actionUiState === "WAITING_SCHEDULING" && waitingCase && waitingOutcome && waitingOutcome !== "Definitívne vyradiť" ? (
          <div className="outcome-scheduling-flow">
            <div className="progressive-heading">
              <p className="eyebrow">Výsledok: {waitingOutcome}</p>
              <h3>{waitingOutcome === "Má záujem" ? "Naplánuj najbližší aktívny krok" : "Kedy sa má FISPRO ozvať znova?"}</h3>
            </div>
            <NextActionScheduler
              today={today}
              value={nextStepDraft}
              onChange={setNextStepDraft}
              onCancel={() => { setWaitingOutcome(null); setActionUiState("WAITING_RESULT"); }}
              onSave={() => {
                onWaitingOutcome(caseItem.id, waitingOutcome, nextStepDraft);
                setWaitingOutcome(null);
                setActionUiState("READY");
              }}
              saveLabel={waitingOutcome === "Má záujem" ? "Vrátiť medzi aktívne" : "Uložiť ďalší kontakt"}
            />
          </div>
        ) : null}

        {actionUiState === "OUTCOME_SCHEDULING" && outcomeDraft && activeOutcomeConfig?.requiresFollowUp ? (
          <div className="outcome-scheduling-flow">
            <div className="progressive-heading">
              <p className="eyebrow">Výsledok: {outcomeDraft.outcome}</p>
              <h3>{outcomeDraft.outcome === "Dohodnuté stretnutie" ? "Kedy sa uskutoční stretnutie?" : "Čo je ďalší krok?"}</h3>
            </div>
            {outcomeDraft.outcome === "Predáva sám" ? (
              <div className="sequence-guidance compact-guidance">
                <div>
                  <span>Podpora hovoru</span>
                  <p>{getOwnerSaleSequenceStep(1)?.recommendedNote}</p>
                </div>
                <div>
                  <span>Princíp</span>
                  <p>Netlačiť na spoluprácu. Dohodnúť presný ďalší kontakt a počúvať reakciu trhu.</p>
                </div>
              </div>
            ) : null}
            <NextActionScheduler
              today={today}
              value={{ date: outcomeDraft.date, time: outcomeDraft.time, endTime: outcomeDraft.endTime, purpose: outcomeDraft.purpose }}
              onChange={(value) => onOutcomeDraftChange({ ...outcomeDraft, ...value })}
              onCancel={() => { onOutcomeDraftChange(null); setActionUiState("CALL_RESULT"); }}
              onSave={() => {
                onSaveOutcome();
                if (shouldShowColdCallClosingReminderAfterSave) setShowColdCallClosingReminder(true);
                setActionUiState("READY");
              }}
              saveLabel={outcomeDraft.outcome === "Dohodnuté stretnutie" ? "Naplánovať stretnutie" : "Uložiť výsledok a ďalší krok"}
            />
          </div>
        ) : null}

        {actionUiState === "OWNER_RESULT" && ownerSaleActive ? (
          <div className="owner-result-form">
            <div className="progressive-heading">
              <p className="eyebrow">Po hovore</p>
              <h3>Zapíš iba podstatné</h3>
            </div>
            <div className="form-grid">
              <TextField
                label="Výsledok hovoru"
                value={ownerSaleContactDraft.result}
                onChange={(result) => setOwnerSaleContactDraft({ ...ownerSaleContactDraft, result })}
                placeholder="Napr. mal dve obhliadky, zatiaľ bez ponuky"
                required
              />
              <label className="text-area-field">
                <span>Poznámka po kontakte</span>
                <textarea
                  value={ownerSaleContactDraft.note}
                  onChange={(event) => setOwnerSaleContactDraft({ ...ownerSaleContactDraft, note: event.target.value })}
                  rows={3}
                  placeholder="Reakcia trhu, námietky alebo dôležitý kontext."
                  required
                />
              </label>
            </div>

            {nextOwnerSaleStep ? (
              <>
                <div className="sequence-next-label">
                  <Clock3 size={16} aria-hidden="true" />
                  Ďalší krok: {nextOwnerSaleStep.title}
                </div>
                <NextActionScheduler
                  today={today}
                  value={{
                    date: ownerSaleContactDraft.nextDate,
                    time: ownerSaleContactDraft.nextTime,
                    purpose: ownerSaleContactDraft.nextPurpose
                  }}
                  onChange={(value) => setOwnerSaleContactDraft({
                    ...ownerSaleContactDraft,
                    nextDate: value.date,
                    nextTime: value.time,
                    nextPurpose: value.purpose
                  })}
                  onCancel={() => setActionUiState("READY")}
                  onSave={() => {
                    if (!canSaveOwnerSaleContact) return;
                    onOwnerSaleContactComplete(caseItem.id, ownerSaleContactDraft);
                    setActionUiState("READY");
                  }}
                  saveLabel="Uložiť výsledok a ďalší kontakt"
                />
              </>
            ) : (
              <>
                <SelectField
                  label="Ukončiť sekvenciu ako"
                  value={ownerSaleContactDraft.endReason}
                  onChange={(endReason) => setOwnerSaleContactDraft({
                    ...ownerSaleContactDraft,
                    endReason: endReason as OwnerSaleContactDraft["endReason"]
                  })}
                  options={OWNER_SALE_SEQUENCE_END_REASONS}
                  required
                />
                <div className="button-row">
                  <button className="secondary-button" type="button" onClick={() => setActionUiState("READY")}>Zrušiť</button>
                  <button
                    className="primary-button"
                    type="button"
                    disabled={!canSaveOwnerSaleContact}
                    onClick={() => {
                      onOwnerSaleContactComplete(caseItem.id, ownerSaleContactDraft);
                      setActionUiState("READY");
                    }}
                  >
                    Uložiť výsledok a ukončiť sekvenciu
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}
        {missingShotScore && !pendingShotScore ? (
          <button
            className="shot-score-missing"
            type="button"
            onClick={() => setPendingShotScore({
              activityId: missingShotScore.id,
              activityType: missingShotScore.activityKind === "viewing" ? "viewing" : "meeting",
              completedAt: missingShotScore.createdAt,
              identity: false,
              value: false,
              acquisition: false,
              nextStep: false
            })}
          >
            Chýba SHOT SCORE k poslednej dokončenej aktivite.
          </button>
        ) : null}
      </section> : null}

      {!excludedCase ? <CaseMarketAnalysisEntry caseItem={caseItem} /> : null}

      {!excludedCase ? <MarketTimelinePanel caseItem={caseItem} onSaveStats={onMarketStatsSave} /> : null}

      {!excludedCase && !pendingRequiredDebrief && caseItem.ownerSaleSequence && ownerSaleStep ? (
        <section className={`owner-sequence-panel ${ownerSaleActive && !caseItem.followUp ? "warning-panel" : ""}`}>
          <PanelHeader eyebrow="Sekvencia" title="Majiteľ predáva sám" icon={Home} />

          <div className="sequence-metrics" aria-label="Stav follow-up sekvencie">
            <div>
              <span>Aktuálny krok</span>
              <strong>{ownerSaleEnded ? "Ukončená" : `${ownerSaleStep.step}/4`}</strong>
            </div>
            <div>
              <span>Dokončené</span>
              <strong>{ownerSaleProgressLabel(caseItem)}</strong>
            </div>
            <div>
              <span>Ďalší kontakt</span>
              <strong>
                {caseItem.followUp
                  ? formatDateTime(caseItem.followUp)
                  : ownerSaleEnded
                    ? caseItem.ownerSaleSequence.endReason
                    : "Chýba termín"}
              </strong>
            </div>
          </div>

          {ownerSaleActive && !caseItem.followUp ? (
            <div className="inline-alert">
              <AlertCircle size={18} aria-hidden="true" />
              Pravidlo FISPRO: majiteľ, ktorý predáva sám, musí mať naplánovaný ďalší presný kontakt alebo ukončenú sekvenciu.
            </div>
          ) : null}

          <div className="sequence-guidance">
            <div>
              <span>Jemný obchodný cieľ</span>
              <p>{ownerSaleStep.softGoal}</p>
            </div>
            <div>
              <span>Čo povedať</span>
              <p>{ownerSaleStep.recommendedNote}</p>
            </div>
            <div>
              <span>Princíp komunikácie</span>
              <p>{ownerSaleStep.principle} Netlačiť na klienta.</p>
            </div>
          </div>

          {ownerSaleActive ? (
            <details className="sequence-end-panel">
              <summary>Ukončiť sekvenciu bez ďalšieho kontaktu</summary>
              <div className="sequence-end-form">
                <SelectField
                  label="Dôvod"
                  value={ownerSaleEndDraft.reason}
                  onChange={(reason) => setOwnerSaleEndDraft({ ...ownerSaleEndDraft, reason: reason as OwnerSaleEndDraft["reason"] })}
                  options={OWNER_SALE_SEQUENCE_END_REASONS}
                />
                <label className="text-area-field">
                  <span>Poznámka k ukončeniu</span>
                  <textarea
                    value={ownerSaleEndDraft.note}
                    onChange={(event) => setOwnerSaleEndDraft({ ...ownerSaleEndDraft, note: event.target.value })}
                    rows={3}
                    placeholder="Prečo sekvencia končí alebo čo je ďalší kontext?"
                  />
                </label>
                <button className="danger-button full-button" type="button" onClick={() => onOwnerSaleSequenceEnd(caseItem.id, ownerSaleEndDraft)}>
                  <XCircle size={17} aria-hidden="true" />
                  Ukončiť sekvenciu
                </button>
              </div>
            </details>
          ) : null}
        </section>
      ) : null}

      {!pendingRequiredDebrief ? <section className="stage-panel">
        <PanelHeader eyebrow="Fáza prípadu" title="Obchodný stav" icon={Target} />
        {waitingCase || excludedCase ? (
          <div className={`lead-state-summary ${excludedCase ? "excluded" : "waiting"}`}>
            <strong>{excludedCase ? "Vyradený" : "Čakajúci"}</strong>
            <span>{excludedCase ? caseItem.exclusionReason ?? caseItem.lostReason ?? "Historicky ukončený prípad" : "Živý lead s naplánovaným ďalším kontaktom"}</span>
          </div>
        ) : (
          <SelectField
            label="Aktuálna fáza"
            value={caseItem.stage}
            onChange={(stageValue) => {
              const stage = stageValue as CaseStage;
              if (stage === "Predané" && caseItem.stage !== "Predané") {
                setIsSelling(true);
                setIsReserving(false);
                return;
              }
              if (stage === "Rezervované" && caseItem.stage !== "Rezervované") {
                setIsSelling(false);
                setReservationUntilDraft("");
                setIsReserving(true);
                return;
              }
              setIsSelling(false);
              setIsReserving(false);
              onStageChange(caseItem.id, stage);
            }}
            options={CASE_STAGE_OPTIONS}
          />
        )}

        {isReserving && caseItem.stage !== "Rezervované" ? (
          <div className="terminal-panel">
            <div className="progressive-heading">
              <p className="eyebrow">Rezervácia</p>
              <h3>Dokedy platí rezervačná zmluva?</h3>
            </div>
            <TextField
              label="Rezervované do"
              type="date"
              min={today}
              value={reservationUntilDraft}
              onChange={setReservationUntilDraft}
              required
            />
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => setIsReserving(false)}>Zrušiť</button>
              <button
                className="primary-button"
                type="button"
                disabled={!reservationUntilDraft || reservationUntilDraft < today}
                onClick={() => {
                  onStageChange(caseItem.id, "Rezervované", reservationUntilDraft);
                  setIsReserving(false);
                }}
              >
                Potvrdiť rezerváciu
              </button>
            </div>
          </div>
        ) : null}

        {isSelling ? (
          <div className="terminal-panel">
            <div className="form-grid">
              <TextField
                label="Realizovaná cena"
                type="number"
                inputMode="numeric"
                value={soldDraft.realizedPrice}
                onChange={(realizedPrice) => setSoldDraft({ ...soldDraft, realizedPrice })}
                required
              />
              <TextField
                label="Dátum predaja"
                type="date"
                value={soldDraft.soldAt}
                onChange={(soldAt) => setSoldDraft({ ...soldDraft, soldAt })}
                required
              />
            </div>
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => setIsSelling(false)}>Zrušiť</button>
              <button
                className="primary-button"
                type="button"
                disabled={!soldDraft.soldAt || Number(soldDraft.realizedPrice) <= 0}
                onClick={() => {
                  onSellCase(caseItem.id, soldDraft);
                  setIsSelling(false);
                }}
              >
                <CheckCircle2 size={17} aria-hidden="true" />
                Uložiť predaj
              </button>
            </div>
          </div>
        ) : null}

        {!excludedCase && caseItem.stage === "Uzavretý" ? (
          <div className="terminal-panel">
            <div className="form-grid three-columns">
              <TextField
                label="Dátum uzavretia"
                type="date"
                value={closingDraft.closedAt}
                onChange={(closedAt) => setClosingDraft({ ...closingDraft, closedAt })}
              />
              <TextField
                label="Skutočná provízia"
                type="number"
                inputMode="numeric"
                value={closingDraft.actualCommission}
                onChange={(actualCommission) => setClosingDraft({ ...closingDraft, actualCommission })}
              />
              <TextField
                label="Poznámka"
                value={closingDraft.note}
                onChange={(note) => setClosingDraft({ ...closingDraft, note })}
              />
            </div>
            <button className="primary-button full-button" type="button" onClick={() => onCloseCase(caseItem.id, closingDraft)}>
              <CheckCircle2 size={17} aria-hidden="true" />
              Uložiť uzavretie
            </button>
          </div>
        ) : null}

        {excludedCase ? (
          <div className="terminal-panel closed-lost-summary">
            <p><strong>Vyradený.</strong> Dôvod: {caseItem.exclusionReason ?? caseItem.lostReason ?? "Historicky ukončený prípad"}.</p>
            <button className="secondary-button full-button" type="button" onClick={() => onRestoreCase(caseItem.id)}>
              <CheckCircle2 size={17} aria-hidden="true" />
              Obnoviť prípad
            </button>
          </div>
        ) : null}
      </section> : null}

      <div className="detail-grid">
        <InfoPanel icon={User} title="Kontakt">
          <p>{caseItem.contactName}</p>
          <a href={`tel:${caseItem.phone}`}>{caseItem.phone}</a>
          {caseItem.email && <a href={`mailto:${caseItem.email}`}>{caseItem.email}</a>}
        </InfoPanel>

        <InfoPanel icon={Home} title="Nehnuteľnosť">
          <p className="context-pair">{caseItem.transactionType ?? "Iné"}</p>
          <p>{propertyDisplayLabel(caseItem)}</p>
          {caseItem.area ? <p>{caseItem.area.toLocaleString("sk-SK")} m²</p> : null}
          <CaseLocationSummary caseItem={caseItem} />
          <span className="muted-line">Cena na inzeráte</span>
          <p>{caseItem.askingPrice > 0 ? formatCurrency(caseItem.askingPrice) : "Neuvedené"}</p>
          <span className="muted-line">Moja odporúčaná cena</span>
          <p>{caseItem.recommendedPrice > 0 ? formatCurrency(caseItem.recommendedPrice) : "Neuvedené"}</p>
          <span className="muted-line">Spolupráca</span>
          <p>{cooperationLabel(caseItem.cooperationType)}</p>
          {caseItem.listingStartedAt ? <span className="muted-line">Zainzerované {formatDate(caseItem.listingStartedAt.slice(0, 10))}</span> : null}
          {caseItem.realizedPrice ? <p>Realizovaná cena {formatCurrency(caseItem.realizedPrice)}</p> : null}
          {caseItem.soldAt ? <span className="muted-line">Predané {formatDate(caseItem.soldAt.slice(0, 10))}</span> : null}
          <button className="secondary-button case-location-edit-button" type="button" onClick={() => setIsEditingCase(true)}>
            <Pencil size={16} aria-hidden="true" />
            Upraviť prípad
          </button>
        </InfoPanel>

        <InfoPanel icon={Target} title="Stav">
          <p>{caseStateLabel(caseItem)}</p>
          {!excludedCase && !waitingCase ? <span className="muted-line">{caseItem.stage}</span> : null}
          <span className="muted-line">{caseItem.priority ?? "Bežný"}</span>
        </InfoPanel>

        <InfoPanel icon={Euro} title="Potenciál provízie">
          <p className="money-value">{formatCurrency(caseItem.commissionPotential)}</p>
          {caseItem.actualCommission ? <span className="muted-line">Skutočne {formatCurrency(caseItem.actualCommission)}</span> : null}
        </InfoPanel>
      </div>

      {isEditingCase ? (
        <form
          className="case-location-editor"
          onSubmit={(event) => {
            event.preventDefault();
            onCaseChange(caseItem.id, editDraft);
            setIsEditingCase(false);
          }}
        >
          <PanelHeader eyebrow="Prípad" title="Upraviť údaje" icon={Pencil} />
          <div className="form-grid">
            <TextField
              label="Meno kontaktu"
              value={editDraft.contactName}
              onChange={(contactName) => setEditDraft({ ...editDraft, contactName })}
              required
            />
            <TextField
              label="Telefón"
              value={editDraft.phone}
              onChange={(phone) => setEditDraft({ ...editDraft, phone })}
              inputMode="tel"
              required
            />
            <TextField
              label="E-mail"
              value={editDraft.email}
              onChange={(email) => setEditDraft({ ...editDraft, email })}
              type="email"
            />
            <SelectField
              label="Typ obchodu"
              value={editDraft.transactionType}
              onChange={(transactionType) => setEditDraft({ ...editDraft, transactionType: transactionType as TransactionType })}
              options={TRANSACTION_TYPES}
            />
            <SelectField
              label="Typ nehnuteľnosti"
              value={editDraft.propertyType}
              onChange={(propertyType) => setEditDraft({ ...editDraft, propertyType: propertyType as PropertyType })}
              options={PROPERTY_TYPES}
            />
            {editDraft.propertyType === "Byt" ? (
              <SelectField
                label="Počet izieb"
                value={editDraft.apartmentRooms}
                onChange={(apartmentRooms) => setEditDraft({ ...editDraft, apartmentRooms: apartmentRooms as ApartmentRooms })}
                options={APARTMENT_ROOM_OPTIONS}
              />
            ) : null}
            <CooperationSelect
              value={editDraft.cooperationType}
              onChange={(cooperationType) => setEditDraft({ ...editDraft, cooperationType })}
            />
            <TextField
              label="Výmera"
              value={editDraft.area}
              onChange={(area) => setEditDraft({ ...editDraft, area })}
              inputMode="numeric"
              type="number"
              placeholder="m²"
            />
          </div>
          <CaseLocationFields value={editDraft} onChange={setEditDraft} cityRequired />
          <div className="form-grid">
            <TextField label="Cena na inzeráte" type="number" inputMode="numeric" value={editDraft.askingPrice} onChange={(askingPrice) => setEditDraft({ ...editDraft, askingPrice })} />
            <TextField label="Moja odporúčaná cena" type="number" inputMode="numeric" value={editDraft.recommendedPrice} onChange={(recommendedPrice) => setEditDraft({ ...editDraft, recommendedPrice })} />
            <TextField label="Potenciál provízie" type="number" inputMode="numeric" value={editDraft.commissionPotential} onChange={(commissionPotential) => setEditDraft({ ...editDraft, commissionPotential })} placeholder="Ak necháš prázdne, počíta sa 3 %" />
          </div>
          <label className="text-area-field">
            <span>Poznámky</span>
            <textarea value={editDraft.notes} onChange={(event) => setEditDraft({ ...editDraft, notes: event.target.value })} rows={4} />
          </label>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={() => {
              setEditDraft(caseEditDraftFromCase(caseItem));
              setIsEditingCase(false);
            }}>
              Zrušiť
            </button>
            <button className="primary-button" type="submit">
              Uložiť prípad
            </button>
          </div>
        </form>
      ) : null}

      <section className="notes-panel">
        <PanelHeader eyebrow="Poznámky" title="Kontext prípadu" icon={StickyNote} />
        <textarea
          value={caseItem.notes}
          onChange={(event) => onNotesChange(caseItem.id, event.target.value)}
          rows={6}
          placeholder="Doplň námietky, motiváciu predaja, cenu, časovanie..."
        />
      </section>

      <Timeline activities={timelineActivities} />
      {pendingShotScore ? (
        <ShotScoreSheet
          draft={pendingShotScore}
          onChange={setPendingShotScore}
          onClose={() => setPendingShotScore(null)}
          onSave={() => {
            onShotScoreSave(caseItem.id, pendingShotScore);
            setPendingShotScore(null);
          }}
        />
      ) : null}
    </section>
  );
}

type ShotScoreSheetProps = {
  draft: ShotScoreDraft;
  onChange: (draft: ShotScoreDraft) => void;
  onClose: () => void;
  onSave: () => void;
};

function ShotScoreSheet({ draft, onChange, onClose, onSave }: ShotScoreSheetProps) {
  const dimensions: Array<{ key: "identity" | "value" | "acquisition" | "nextStep"; title: string; body: string }> = [
    { key: "identity", title: "Identita", body: "Klient vie, že robím reality + financie" },
    { key: "value", title: "Hodnota", body: "Ponúkol som relevantnú konkrétnu hodnotu/službu" },
    { key: "acquisition", title: "Náber", body: "Otestoval som spoluprácu/náber" },
    { key: "nextStep", title: "Next step", body: "Dohodol som ďalší krok alebo dostal jasné NIE" }
  ];

  return (
    <div className="call-reminder-backdrop" role="presentation">
      <section className="shot-score-sheet" role="dialog" aria-modal="true" aria-labelledby="shot-score-title">
        <div className="shot-score-heading">
          <div>
            <p className="eyebrow">Exekúcia makléra</p>
            <h2 id="shot-score-title">SHOT SCORE</h2>
          </div>
          <strong>{shotScoreValue(draft)} %</strong>
        </div>
        <p className="shot-score-copy">Bod získavaš za vykonanie kroku, nie za rozhodnutie klienta.</p>
        <div className="shot-score-options">
          {dimensions.map((dimension) => (
            <label key={dimension.key}>
              <input
                type="checkbox"
                checked={draft[dimension.key]}
                onChange={(event) => onChange({ ...draft, [dimension.key]: event.target.checked })}
              />
              <span><strong>{dimension.title}</strong><small>{dimension.body}</small></span>
            </label>
          ))}
        </div>
        <div className="button-row">
          <button className="secondary-button" type="button" onClick={onClose}>Neskôr</button>
          <button className="primary-button" type="button" onClick={onSave}>Uložiť</button>
        </div>
      </section>
    </div>
  );
}

export function CallFirstPrompt({ caseItem, onClose }: { caseItem: RealEstateCase; onClose: () => void }) {
  return (
    <div className="call-reminder-backdrop" role="presentation">
      <section className="call-first-sheet" role="dialog" aria-modal="true" aria-labelledby="call-first-title">
        <div className="call-reminder-icon" aria-hidden="true"><PhoneCall size={20} /></div>
        <div>
          <p className="eyebrow">Naplánovaný telefonát</p>
          <h2 id="call-first-title">Zavolať {caseItem.contactName}?</h2>
        </div>
        <a className="primary-button full-button" href={`tel:${caseItem.phone}`} onClick={onClose}>
          <PhoneCall size={18} aria-hidden="true" />
          Zavolať teraz
        </a>
        <button className="secondary-button full-button" type="button" onClick={onClose}>Otvoriť prípad</button>
      </section>
    </div>
  );
}

type PipelineScreenProps = {
  cases: RealEstateCase[];
  onOpenCase: (caseId: string) => void;
};

export function PipelineScreen({ cases, onOpenCase }: PipelineScreenProps) {
  const groupedCases = pipelineGroups.map((group) => {
    const stageCases = cases.filter((caseItem) => isLiveCase(caseItem) && group.stages.includes(caseItem.stage));
    return {
      ...group,
      cases: stageCases,
      potential: stageCases.reduce((sum, caseItem) => sum + caseItem.commissionPotential, 0)
    };
  });

  return (
    <section className="pipeline-screen">
      <div className="pipeline-command">
        <div>
          <p className="eyebrow">Pipeline</p>
          <h2>Obchodné fázy prípadu</h2>
        </div>
        <span className="soft-badge">
          <Euro size={14} aria-hidden="true" />
          {formatCurrency(groupedCases.reduce((sum, group) => sum + group.potential, 0))}
        </span>
      </div>

      <div className="pipeline-board" aria-label="Pipeline prípadov">
        {groupedCases.map((group, index) => {
          const StageIcon = group.icon;
          return (
            <section className={`pipeline-column stage-tone-${(index % 6) + 1}`} key={group.title}>
              <div className="pipeline-column-header">
                <div>
                  <span className="stage-number">{String(index + 1).padStart(2, "0")}</span>
                  <div className="stage-title-row">
                    <StageIcon size={18} aria-hidden="true" />
                    <h3>{group.title}</h3>
                  </div>
                  <p>{group.description}</p>
                </div>
                <div className="stage-summary">
                  <strong>{group.cases.length}</strong>
                  <small>{formatCurrency(group.potential)}</small>
                </div>
              </div>

              <div className="pipeline-stack">
                {group.cases.length > 0 ? (
                  group.cases.map((caseItem) => (
                    <button className="pipeline-card" key={caseItem.id} type="button" onClick={() => onOpenCase(caseItem.id)}>
                      <PropertyMedia
                        className="pipeline-card-media"
                        propertyType={caseItem.propertyType}
                        seed={caseItem.id}
                        imageUrl={caseImageUrl(caseItem)}
                        label={propertyDisplayLabel(caseItem)}
                      />
                      <span className="pipeline-card-name">{caseItem.contactName}</span>
                      <small>{caseContextLabel(caseItem)}</small>
                      <strong>{formatCurrency(caseItem.commissionPotential)}</strong>
                    </button>
                  ))
                ) : (
                  <div className="pipeline-empty">
                    <KeyRound size={16} aria-hidden="true" />
                    Bez prípadu
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}

type SettingsScreenProps = {
  totalCases: number;
  activeCaseCount: number;
  waitingCaseCount: number;
  excludedCaseCount: number;
  dataWipeStep: number;
  accountEmail?: string;
  cloudConfigured: boolean;
  syncStatus: SyncStatus;
  notificationStatus: PushPermissionStatus;
  notificationPermission: PushPermissionLabel;
  notificationError: string;
  notificationMessage: string;
  notificationsWorking: boolean;
  notificationDeviceRegistered: boolean;
  notificationLastSuccessfulPushAt?: string;
  notificationLastFailureAt?: string;
  notificationLastErrorCategory?: string;
  isInstalledPwa: boolean;
  isIosDevice: boolean;
  shotScoreAnalytics: ShotScoreAnalytics;
  onEnableNotifications: () => void;
  onDisableNotifications: () => void;
  onSendTestNotification: () => void;
  onLogout: () => void;
  onStartWipe: () => void;
  onAdvanceWipe: () => void;
  onCancelWipe: () => void;
  onConfirmWipe: () => void;
};

export function SettingsScreen({
  totalCases,
  activeCaseCount,
  waitingCaseCount,
  excludedCaseCount,
  dataWipeStep,
  accountEmail,
  cloudConfigured,
  syncStatus,
  notificationStatus,
  notificationPermission,
  notificationError,
  notificationMessage,
  notificationsWorking,
  notificationDeviceRegistered,
  notificationLastSuccessfulPushAt,
  notificationLastFailureAt,
  notificationLastErrorCategory,
  isInstalledPwa,
  isIosDevice,
  shotScoreAnalytics,
  onEnableNotifications,
  onDisableNotifications,
  onSendTestNotification,
  onLogout,
  onStartWipe,
  onAdvanceWipe,
  onCancelWipe,
  onConfirmWipe
}: SettingsScreenProps) {
  const formatNotificationTime = (value?: string) => {
    if (!value) return "Zatiaľ bez úspešného pushu";
    return new Intl.DateTimeFormat("sk-SK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(value));
  };
  const statusClass = notificationStatus.toLocaleLowerCase("sk").replace(/\s+/g, "-");
  return (
    <section className="screen-grid settings-screen">
      <section className="settings-panel">
        <div className="settings-hero">
          <div className="settings-orbit" aria-hidden="true">
            <SettingsIcon size={22} />
          </div>
          <div>
            <p className="eyebrow">System panel</p>
            <h2>Dáta v tomto zariadení</h2>
          </div>
        </div>

        <div className="settings-meta" aria-label="Počet uložených prípadov">
          <div className="settings-meta-icon" aria-hidden="true">
            <FileText size={18} />
          </div>
          <div>
            <span>Uložené prípady</span>
            <p>V tomto zariadení</p>
          </div>
          <strong>{totalCases}</strong>
        </div>

        <div className="case-state-stats" aria-label="Obchodné stavy prípadov">
          <div><span>Aktívne</span><strong>{activeCaseCount}</strong></div>
          <div><span>Čakajúce</span><strong>{waitingCaseCount}</strong></div>
          <div><span>Vyradené</span><strong>{excludedCaseCount}</strong></div>
        </div>

        <div className="settings-account" aria-label="Cloud účet a stav synchronizácie">
          <div className="settings-account-main">
            <div className="settings-meta-icon" aria-hidden="true">
              {syncStatus === "Offline" ? <CloudOff size={18} /> : <Cloud size={18} />}
            </div>
            <div>
              <span>Cloud účet</span>
              <p>{accountEmail ?? (cloudConfigured ? "Neprihlásený" : "Supabase nie je nakonfigurovaný")}</p>
            </div>
            <strong className={`sync-status sync-${syncStatus.toLowerCase()}`}>{syncStatus}</strong>
          </div>

          {accountEmail ? (
            <button className="secondary-button full-button" type="button" onClick={onLogout}>
              <LogOut size={17} aria-hidden="true" />
              Odhlásiť sa
            </button>
          ) : null}
        </div>

        <div className="settings-notifications" aria-label="Nastavenie push upozornení">
          <div className="settings-notifications-main">
            <div className="settings-meta-icon" aria-hidden="true">
              <BellRing size={18} />
            </div>
            <div>
              <span>Upozornenia</span>
              <p>Serverové pripomenutie pri termíne a raz po uplynutí lehoty.</p>
            </div>
            <strong className={`notification-status notification-${statusClass}`}>
              {notificationStatus}
            </strong>
          </div>

          <div className="notification-device-status">
            <span>Toto zariadenie</span>
            <strong>{notificationDeviceRegistered ? "Registrované" : "Nezaregistrované"}</strong>
            <small>Povolenie systému: {notificationPermission}</small>
            {isIosDevice ? <small>Home Screen PWA: {isInstalledPwa ? "Áno" : "Nie"}</small> : null}
            <small>Posledný úspešný push: {formatNotificationTime(notificationLastSuccessfulPushAt)}</small>
            {notificationLastFailureAt ? (
              <small>Posledné zlyhanie: {formatNotificationTime(notificationLastFailureAt)}{notificationLastErrorCategory ? ` · ${notificationLastErrorCategory}` : ""}</small>
            ) : null}
          </div>

          {notificationStatus !== "Zapnuté" || !notificationDeviceRegistered ? (
            <button
              className="secondary-button full-button"
              type="button"
              onClick={onEnableNotifications}
              disabled={notificationsWorking || notificationStatus === "Povolenie zamietnuté"}
            >
              <BellRing size={17} aria-hidden="true" />
              {notificationsWorking ? "Zapínam..." : "Zapnúť notifikácie"}
            </button>
          ) : (
            <div className="notification-actions">
              <button className="secondary-button" type="button" onClick={onSendTestNotification} disabled={notificationsWorking}>
                <BellRing size={17} aria-hidden="true" />
                Poslať testovaciu notifikáciu
              </button>
              <button className="secondary-button" type="button" onClick={onDisableNotifications} disabled={notificationsWorking}>
                Vypnúť notifikácie
              </button>
            </div>
          )}

          {notificationError ? <p className="notification-error" aria-live="polite">{notificationError}</p> : null}
          {notificationMessage ? <p className="notification-message" aria-live="polite">{notificationMessage}</p> : null}
        </div>

        <div className="shot-score-analytics" aria-label="SHOT SCORE za posledných 30 aktivít">
          <div className="shot-score-analytics-heading">
            <div>
              <span>SHOT SCORE</span>
              <p>Posledných {shotScoreAnalytics.sampleSize} stretnutí a obhliadok</p>
            </div>
            <strong>{shotScoreAnalytics.overall} %</strong>
          </div>
          <div className="shot-score-metrics">
            <span>Identita <strong>{shotScoreAnalytics.identity} %</strong></span>
            <span>Hodnota <strong>{shotScoreAnalytics.value} %</strong></span>
            <span>Náber <strong>{shotScoreAnalytics.acquisition} %</strong></span>
            <span>Next step <strong>{shotScoreAnalytics.nextStep} %</strong></span>
          </div>
        </div>

        <p className="settings-copy">
          Dáta sa ukladajú okamžite v tomto zariadení a po prihlásení sa bezpečne synchronizujú cez Supabase.
        </p>

        {dataWipeStep === 0 && (
          <div className="danger-zone">
            <div className="danger-title-row">
              <Shield size={18} aria-hidden="true" />
              <div>
                <span>Riziková akcia</span>
                <p>Vymazanie prebehne až po dvoch potvrdeniach.</p>
              </div>
            </div>
            <button className="danger-button full-button" type="button" onClick={onStartWipe}>
              <Trash2 size={18} aria-hidden="true" />
              Vymazať všetky dáta
            </button>
          </div>
        )}

        {dataWipeStep === 1 && (
          <div className="confirmation-panel">
            <div className="inline-alert danger-alert">
              <AlertCircle size={18} aria-hidden="true" />
              Prvé potvrdenie: pripravuješ vymazanie všetkých uložených prípadov.
            </div>
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={onCancelWipe}>
                Zrušiť
              </button>
              <button className="danger-button" type="button" onClick={onAdvanceWipe}>
                Pokračovať
              </button>
            </div>
          </div>
        )}

        {dataWipeStep === 2 && (
          <div className="confirmation-panel">
            <div className="inline-alert danger-alert">
              <AlertCircle size={18} aria-hidden="true" />
              Druhé potvrdenie: táto akcia odstráni prípady lokálne aj zo synchronizovaných zariadení.
            </div>
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={onCancelWipe}>
                Zrušiť
              </button>
              <button className="danger-button" type="button" onClick={onConfirmWipe}>
                Áno, vymazať všetky dáta
              </button>
            </div>
          </div>
        )}
      </section>
    </section>
  );
}

type CaseCardProps = {
  caseItem: RealEstateCase;
  today?: string;
  compact?: boolean;
  onOpenCase: (caseId: string) => void;
  onAddNextStep?: (caseId: string) => void;
};

function CaseCard({ caseItem, today, compact = false, onOpenCase, onAddNextStep }: CaseCardProps) {
  const missingStep = isMissingNextStep(caseItem);
  const pendingRequiredDebrief = getPendingRequiredDebrief(caseItem);
  const actionableMissingStep = missingStep && Boolean(onAddNextStep);
  const ownerSaleStep = isOwnerSaleSequenceActive(caseItem) ? getCurrentOwnerSaleSequenceStep(caseItem) : undefined;
  const overdue = today ? overdueTimeLabel(caseItem.followUp, new Date()) : "";

  return (
    <article className={`case-card ${compact ? "agenda-card" : ""} ${caseItem.priority === "HOT" ? "hot-card" : ""} ${missingStep ? "missing-card" : ""} ${actionableMissingStep ? "actionable-missing-card" : ""}`}>
      <button className="case-card-open" type="button" onClick={() => actionableMissingStep ? onAddNextStep?.(caseItem.id) : onOpenCase(caseItem.id)}>
        <PropertyMedia
          className="case-card-media"
          propertyType={caseItem.propertyType}
          seed={caseItem.id}
          imageUrl={caseImageUrl(caseItem)}
          label={propertyDisplayLabel(caseItem)}
        />
        <div className="case-card-body">
          <div className="case-card-main">
            <div className="case-card-copy">
              <span className="status-pill">{caseStateLabel(caseItem)}</span>
              <h3>{caseItem.contactName}</h3>
              <p className="case-context">
                <KeyRound size={13} aria-hidden="true" />
                {caseContextLabel(caseItem)}
              </p>
              <small>
                <MapPin size={13} aria-hidden="true" />
                {caseLocationLabel(caseItem)}
              </small>
            </div>
            <ChevronRight size={19} aria-hidden="true" />
          </div>
          <div className="case-card-footer">
            <span>
              <NextStepIcon caseItem={caseItem} size={14} />
              <div className="next-step-copy">
                <span>{pendingRequiredDebrief
                  ? "Stretnutie – čaká na debrief"
                  : isReservationControl(caseItem)
                    ? RESERVATION_DUE_LABEL
                    : caseItem.stage === "Rezervované"
                      ? "Chýba dátum rezervácie"
                    : nextStepLabel(caseItem.followUp, caseItem.nextStep)}</span>
                {overdue ? <em>{overdue}</em> : null}
              </div>
            </span>
            <strong aria-label="Potenciál provízie">
              <Euro size={14} aria-hidden="true" />
              {formatCurrency(caseItem.commissionPotential)}
            </strong>
          </div>
          {ownerSaleStep ? (
            <div className="sequence-card-hint">
              <Home size={14} aria-hidden="true" />
              <span>{ownerSaleStep.recommendedNote}</span>
            </div>
          ) : null}
          {actionableMissingStep ? (
            <span className="missing-step-cta">{caseItem.stage === "Rezervované" ? "Doplniť dátum rezervácie" : "Doplniť ďalší krok"} <ChevronRight size={15} aria-hidden="true" /></span>
          ) : null}
        </div>
      </button>
      <div className="case-card-actions">
        {!compact ? (
          <a className="mini-action primary" href={`tel:${caseItem.phone}`}>
            <PhoneCall size={15} aria-hidden="true" />
            Volať
          </a>
        ) : null}
        <button className="mini-action" type="button" onClick={() => onOpenCase(caseItem.id)}>
          <FileText size={15} aria-hidden="true" />
          {compact ? "Otvoriť krok" : "Detail"}
        </button>
      </div>
    </article>
  );
}

type TimelineProps = {
  activities: RealEstateCase["timeline"];
};

function caseEditDraftFromCase(caseItem: RealEstateCase): CaseEditDraft {
  return {
    contactName: caseItem.contactName,
    phone: caseItem.phone,
    email: caseItem.email,
    transactionType: caseItem.transactionType ?? "Iné",
    propertyType: PROPERTY_TYPES.includes(caseItem.propertyType as PropertyType)
      ? (caseItem.propertyType as PropertyType)
      : "Iné",
    apartmentRooms: caseItem.apartmentRooms ?? "2-izbový",
    cooperationType: caseItem.cooperationType ?? "unspecified",
    city: caseItem.city ?? "",
    cityNormalized: caseItem.cityNormalized ?? "",
    citySource: caseItem.citySource,
    municipalityId: caseItem.municipalityId,
    street: caseItem.street ?? "",
    streetNormalized: caseItem.streetNormalized ?? "",
    streetSource: caseItem.streetSource,
    streetId: caseItem.streetId,
    locality: caseItem.locality ?? "",
    propertyAddress: caseItem.propertyAddress,
    area: caseItem.area ? String(caseItem.area) : "",
    askingPrice: caseItem.askingPrice > 0 ? String(caseItem.askingPrice) : "",
    recommendedPrice: caseItem.recommendedPrice > 0 ? String(caseItem.recommendedPrice) : "",
    commissionPotential: caseItem.commissionPotential > 0 ? String(caseItem.commissionPotential) : "",
    notes: caseItem.notes
  };
}

const cooperationLabels: Record<CooperationType, string> = {
  exclusive: "Exkluzívna",
  nonExclusive: "Neexkluzívna",
  unspecified: "Neuvedené"
};

function cooperationLabel(value?: CooperationType) {
  return cooperationLabels[value ?? "unspecified"];
}

function CooperationSelect({ value, onChange }: { value: CooperationType; onChange: (value: CooperationType) => void }) {
  return (
    <label className="text-field select-field">
      <span>Typ spolupráce</span>
      <select value={value} onChange={(event) => onChange(event.target.value as CooperationType)}>
        {COOPERATION_TYPES.map((option) => <option key={option} value={option}>{cooperationLabels[option]}</option>)}
      </select>
      <ChevronDown className="select-chevron" size={16} aria-hidden="true" />
    </label>
  );
}

type CaseLocationFieldsProps<T extends CaseLocationDraft> = {
  value: T;
  onChange: (value: T) => void;
  cityRequired?: boolean;
};

function CaseLocationFields<T extends CaseLocationDraft>({
  value,
  onChange,
  cityRequired = false
}: CaseLocationFieldsProps<T>) {
  return (
    <fieldset className="case-location-fields">
      <legend>
        <MapPin size={15} aria-hidden="true" />
        Lokalita nehnuteľnosti
      </legend>
      <div className="form-grid case-location-grid">
        <CityAutocomplete
          value={value.city}
          citySource={value.citySource}
          required={cityRequired}
          onChange={(selection) => onChange(locationAfterCityChange(value, selection))}
        />
        <StreetAutocomplete
          value={value.street}
          municipalityId={value.municipalityId}
          streetId={value.streetId}
          streetSource={value.streetSource}
          onChange={(selection) => onChange({ ...value, ...selection })}
        />
        <TextField
          label="Lokalita / mikrolokalita"
          value={value.locality}
          onChange={(locality) => onChange({ ...value, locality })}
          placeholder="Napr. Staré sídlisko"
        />
      </div>
    </fieldset>
  );
}

function CaseLocationSummary({ caseItem }: { caseItem: RealEstateCase }) {
  const hasStructuredLocation = Boolean(caseItem.street || caseItem.city || caseItem.locality);

  if (!hasStructuredLocation) {
    return caseItem.propertyAddress ? <p>{caseItem.propertyAddress}</p> : <p className="muted-line">Lokalita neuvedená</p>;
  }

  return (
    <div className="case-location-summary">
      {caseItem.street ? <p>{caseItem.street}</p> : null}
      {caseItem.city ? <p>{caseItem.city}</p> : null}
      {caseItem.locality ? <span className="muted-line">{caseItem.locality}</span> : null}
    </div>
  );
}

function caseLocationLabel(caseItem: RealEstateCase) {
  return [caseItem.street, caseItem.city].filter(Boolean).join(", ") || caseItem.propertyAddress || "Lokalita neuvedená";
}

function Timeline({ activities }: TimelineProps) {
  return (
    <section className="timeline-panel">
      <PanelHeader eyebrow="Timeline" title="Aktivity prípadu" icon={BellRing} />
      <div className="timeline">
        {activities.map((activity) => {
          const ActivityIcon = timelineIcon(activity);
          return (
            <article className="timeline-item" key={activity.id}>
              <time dateTime={activity.createdAt}>
                {new Intl.DateTimeFormat("sk-SK", {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit"
                }).format(new Date(activity.createdAt))}
              </time>
              <div className="timeline-icon" aria-hidden="true">
                <ActivityIcon size={15} />
              </div>
              <div>
                <h3>{activity.title}</h3>
                <p>{activity.body}</p>
                {activity.followUp ? <small>{formatDateTime(activity.followUp)}</small> : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

type InfoPanelProps = {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
};

function InfoPanel({ icon: Icon, title, children }: InfoPanelProps) {
  return (
    <section className="info-panel">
      <div className="info-title">
        <Icon size={18} aria-hidden="true" />
        <h3>{title}</h3>
      </div>
      <div className="info-body">{children}</div>
    </section>
  );
}

type EmptyStateProps = {
  icon: LucideIcon;
  title?: string;
  body?: string;
  actionLabel?: string;
  onAction?: () => void;
};

function EmptyState({ icon: Icon, title, body, actionLabel, onAction }: EmptyStateProps) {
  return (
    <section className="empty-state">
      <div className="empty-visual" aria-hidden="true">
        <div className="empty-icon">
          <Icon size={24} aria-hidden="true" />
        </div>
      </div>
      {title ? <h2>{title}</h2> : null}
      {body ? <p>{body}</p> : null}
      {actionLabel && onAction ? (
        <button className="primary-button" type="button" onClick={onAction}>
          <Plus size={18} aria-hidden="true" />
          {actionLabel}
        </button>
      ) : null}
    </section>
  );
}

type MetricProps = {
  icon: LucideIcon;
  label: string;
  value: string;
};

function Metric({ icon: Icon, label, value }: MetricProps) {
  return (
    <div className="metric">
      <div aria-hidden="true">
        <Icon size={16} />
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

type PanelHeaderProps = {
  eyebrow: string;
  title: string;
  icon: LucideIcon;
};

function PanelHeader({ eyebrow, title, icon: Icon }: PanelHeaderProps) {
  return (
    <div className="section-heading compact">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <Icon size={20} aria-hidden="true" />
    </div>
  );
}

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  inputMode?: "text" | "tel" | "numeric";
  min?: string;
};

function TextField({ label, value, onChange, type = "text", required, placeholder, inputMode = "text", min }: TextFieldProps) {
  return (
    <label className="text-field">
      <span>{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        min={min}
      />
    </label>
  );
}

type SelectFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  required?: boolean;
};

function SelectField({ label, value, onChange, options, required }: SelectFieldProps) {
  return (
    <label className="text-field select-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} required={required}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ChevronDown className="select-chevron" size={16} aria-hidden="true" />
    </label>
  );
}

type ChipButtonProps = {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
};

function ChipButton({ active, children, onClick }: ChipButtonProps) {
  return (
    <button className={`chip-button ${active ? "active" : ""}`} type="button" onClick={onClick}>
      {children}
    </button>
  );
}

type NavButtonProps = {
  active: boolean;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
};

function NavButton({ active, icon: Icon, label, onClick }: NavButtonProps) {
  return (
    <button className={active ? "active" : ""} type="button" onClick={onClick} aria-current={active ? "page" : undefined}>
      <Icon size={20} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function screenTitle(screen: Screen) {
  if (screen === "now") {
    return "Teraz";
  }

  if (screen === "today") {
    return "Dnes";
  }

  if (screen === "cases") {
    return "Prípady";
  }

  if (screen === "pipeline") {
    return "Pipeline";
  }

  if (screen === "settings") {
    return "Nastavenia";
  }

  return "Prípad detail";
}

function outcomeIcon(outcome: CallOutcome): LucideIcon {
  if (outcome === "Predáva sám") {
    return Home;
  }

  if (outcome === "Nedvihol" || outcome === "Zavolať neskôr") {
    return PhoneCall;
  }

  if (outcome === "Dohodnuté stretnutie") {
    return Users;
  }

  if (outcome === "Nezáujem") {
    return XCircle;
  }

  return CheckCircle2;
}

function timelineIcon(activity: TimelineActivity): LucideIcon {
  if (activity.type === "shot-score") {
    return Target;
  }
  if (activity.type === "owner-sale" || activity.outcome === "Predáva sám") {
    return Home;
  }

  if (activity.type === "call" || activity.outcome) {
    return PhoneCall;
  }

  if (activity.type === "follow-up" || activity.followUp) {
    return CalendarDays;
  }

  if (activity.type === "stage") {
    return Target;
  }

  if (activity.type === "closed") {
    return activity.title.toLowerCase().includes("odmiet") ? XCircle : CheckCircle2;
  }

  if (activity.type === "priority") {
    return Sparkles;
  }

  if (activity.type === "note") {
    return StickyNote;
  }

  return FileText;
}

function NextStepIcon({ caseItem, size }: { caseItem: RealEstateCase; size: number }) {
  const purpose = `${caseItem.followUp?.purpose ?? caseItem.nextStep}`.toLowerCase();
  if (purpose.includes("stretn") || caseItem.stage === "Stretnutie") {
    return <Users size={size} aria-hidden="true" />;
  }

  if (purpose.includes("obhliad") || caseItem.stage === "Obhliadka") {
    return <Home size={size} aria-hidden="true" />;
  }

  if (purpose.includes("predáva sám") || caseItem.status === "Predáva sám") {
    return <Home size={size} aria-hidden="true" />;
  }

  if (purpose.includes("call") || purpose.includes("zavola") || purpose.includes("hovor")) {
    return <PhoneCall size={size} aria-hidden="true" />;
  }

  if (!caseItem.followUp) {
    return <AlertCircle size={size} aria-hidden="true" />;
  }

  return <CalendarDays size={size} aria-hidden="true" />;
}

function propertyImageGroup(propertyType?: string) {
  if (propertyType === "Rodinný dom" || propertyType === "Rekreačná nehnuteľnosť") {
    return "house";
  }

  if (propertyType === "Byt" || propertyType === "Novostavba") {
    return "apartment";
  }

  if (propertyType === "Pozemok") {
    return "land";
  }

  if (
    propertyType === "Komerčný priestor" ||
    propertyType === "Administratívny priestor" ||
    propertyType === "Garáž" ||
    propertyType === "Developerský projekt"
  ) {
    return "commercial";
  }

  return "general";
}

function legacyPropertyImageGroup(propertyType?: string) {
  if (
    propertyType === "Rodinný dom" ||
    propertyType === "Rekreačná nehnuteľnosť" ||
    propertyType === "Developerský projekt" ||
    propertyType === "Pozemok"
  ) {
    return "house";
  }

  if (propertyType === "Byt" || propertyType === "Novostavba") {
    return "apartment";
  }

  return "general";
}

function propertyImagePool(propertyType?: string): readonly string[] {
  const group = propertyImageGroup(propertyType);

  if (group === "house") {
    return housePropertyImages;
  }

  if (group === "apartment") {
    return apartmentPropertyImages;
  }

  if (group === "land") {
    return landPropertyImages;
  }

  if (group === "commercial") {
    return commercialPropertyImages;
  }

  return propertyImageSources;
}

function propertyImageFor(propertyType?: string, seed = "") {
  const imagePool = propertyImagePool(propertyType);
  const imageIndex = stableCaseImageIndex(seed, imagePool.length);
  return imagePool[imageIndex % imagePool.length];
}

function caseImageUrl(caseItem: RealEstateCase) {
  const imageUrl = (caseItem as RealEstateCase & { imageUrl?: unknown }).imageUrl;
  return typeof imageUrl === "string" ? imageUrl : undefined;
}

function TodayHeroMedia({ label }: { label: string }) {
  return (
    <div className="property-media hero-property-media today-hero-media">
      <img src={todayHeroImage} alt="" aria-hidden="true" loading="eager" decoding="async" />
      <span>{label}</span>
    </div>
  );
}

function PropertyMedia({
  className,
  propertyType,
  seed,
  label,
  eager = false,
  imageUrl
}: {
  className: string;
  propertyType?: string;
  seed?: string;
  label: string;
  eager?: boolean;
  imageUrl?: string;
}) {
  const assignedSources = useContext(CaseImageSourceContext);
  const assignedSource = seed ? assignedSources?.get(seed) : undefined;

  return (
    <div className={`property-media ${className}`}>
      <img
        src={imageUrl?.trim() || assignedSource || propertyImageFor(propertyType, seed ?? "")}
        alt=""
        aria-hidden="true"
        loading={eager ? "eager" : "lazy"}
        decoding="async"
      />
      <span>{label}</span>
    </div>
  );
}
